<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type Component } from 'vue';
import {
  IconArrowDown,
  IconArrowUp,
  IconAttach,
  IconClose,
  IconComment,
  IconDelete,
  IconAlert,
  IconDocument,
  IconDocumentText,
  IconEdit,
  IconEye,
  IconFlash,
  IconHistory,
  IconLock,
  IconSettings,
  IconUnlock,
} from '@/components/design/icons';
import { useThemeVars } from 'naive-ui';
import {
  ChatAttentionReason,
  ChatClientSurface,
  ChatConfirmationMode,
  deleteThread,
  deleteThread2,
  deleteThread3,
  getThreadById,
  getThreadById2,
  getThreadById3,
  PreprocessingTaskStatus,
  getContractManagementContract,
  getContractManagementContractFile,
  getVaultFileMetadata,
  searchThreads,
  searchThreads2,
  searchProjectThreads,
  TaskStatus,
  updateThread,
  updateThread2,
  updateThread3,
  type VaultFileInDb,
  type GeneralChatStreamRequest,
  type ChatThreadInDb,
  type ChatThreadUpdate,
  type ChatThreadWithMessages,
  type LimitableSearchableQuery,
  type SearchProjectThreadsResponses,
  type SearchProjectThreadsErrors,
} from '@/api';
import type { RequestResult } from '@/api/client';
import {
  getWsClient,
  type ChatResumeRequestBody,
  type ChatStreamRequestBody,
} from '@/services/WebSocketService';
import {
  useModelSelection,
  formatModelLabel,
  formatModelProviderLabel,
  getModelOptionKey,
} from '@/composables/useModelSelection';
import {
  useChatStream,
  ChatStreamCancelledError,
  ChatStreamServerError,
} from '@/composables/useChatStream';
import { useChatStatusGroups } from '@/composables/useChatStatusGroups';
import { useUserAttentionStore } from '@/stores/userAttentionStore';
import { useGaiaStore } from '@/stores/gaiaStore';
import { useVaultStore } from '@/stores/vaultStore';
import { useReviewProjectStore } from '@/stores/reviewProjectStore';
import { useOrganizationStore } from '@/stores/organizationStore';
import { RouterLink, useRoute, useRouter, type LocationQueryValue } from 'vue-router';
import { isOfficeAddinHost } from '@/office/utils';
import { insertTextAtSelection } from '@/office/word/templateWord';
import { executeDocumentTool } from '@/office/word/documentTools';
import HitlInterruptCard, {
  type HitlSubmitPayload,
} from '@/components/humanInTheLoop/HitlInterruptCard.vue';
import MarkdownRenderer, { type MarkdownCodeAction } from '@/components/MarkdownRenderer.vue';
import { isEnumValue } from '@/utils/enumUtils';
import { toRecord, parseJsonIfString, getString } from '@/utils/jsonHelpers';
import {
  normalizeInterruptPayload,
  type NormalizedInterrupt,
} from '@/utils/humanInTheLoop/normalizeInterrupt';
import { logger } from '@/utils/logger';
import { MAX_ASK_AI_ITEMS } from '@/utils/askAi';
import { useVaultUploadDialog } from '@/composables/useVaultUploadDialog';
import { isArchive } from '@/utils/fileTypeUtils';
import { useVaultWebSocket } from '@/composables/useVaultWebSocket';
import GaiaSwitch from '@/components/design/base/GaiaSwitch.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaPromptTextarea from '@/components/design/base/inputs/GaiaPromptTextarea.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

type ChatRole = 'user' | 'assistant' | 'system' | 'tool';

interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  wordDocumentContext?: string;
  wordSelectionContext?: string;
  selectedDocumentsIncluded?: boolean;
  selectedDocumentCount?: number;
  selectedDocumentNames?: string[];
  /** Context keys (`file-`/`contract-`/`cfile-<uid>`) whose names resolve client-side. */
  selectedDocumentKeys?: string[];
  interrupt?: InterruptMessageState;
  /** The run that wrote this message failed: `content` is the failure copy, not an answer. */
  failed?: boolean;
}

/**
 * Lifecycle of a composer reference. A vault file only becomes readable once the
 * backend's preprocessing has written its chunks (`ocr_status: DONE`) — before that the
 * document tools find nothing, so the composer holds the message back rather than let
 * the assistant report an empty file.
 */
type SelectedContextStatus = 'uploading' | 'processing' | 'ready' | 'failed';

// What the general chat can be pointed at. Vault directories are deliberately absent:
// the backend resolves references to concrete revisions and no longer accepts folders.
type SelectedContextType = 'file' | 'contract' | 'contractFile' | 'gaia';

type OfficeSelectedTextResult = {
  error?: { message?: string };
  status: unknown;
  value?: string;
};

type OfficeVoidResult = {
  error?: { message?: string };
  status: unknown;
};

type WordSelectionRange = {
  insertComment?: (commentText: string) => unknown;
  load: (propertyNames: string) => void;
  text?: string;
};

type WordRunContext = {
  document: {
    getSelection: () => WordSelectionRange;
  };
  sync: () => Promise<void>;
};

type WordApi = {
  run: (callback: (context: WordRunContext) => Promise<void>) => Promise<void>;
};

interface InterruptMessageState {
  interrupts: NormalizedInterrupt[];
}

interface SelectedContextMeta {
  name: string;
  kind: string;
  type: SelectedContextType;
}

interface ConfirmationModeOption {
  mode: ChatConfirmationMode;
  label: string;
  description: string;
  icon: Component;
  colorKey: 'successColor' | 'infoColor' | 'warningColor';
}

const CONFIRMATION_MODE_DEFAULT = ChatConfirmationMode.READ;

const CONFIRMATION_MODE_OPTIONS: ConfirmationModeOption[] = [
  {
    // Shown as "Manual" — the wire value stays `read` (ChatConfirmationMode.READ),
    // which is what the backend and the stored preference both key on.
    mode: ChatConfirmationMode.READ,
    label: 'Manual',
    description: 'Only actions that change data pause for approval.',
    icon: IconEye,
    colorKey: 'infoColor',
  },
  {
    mode: ChatConfirmationMode.AUTO,
    label: 'Auto',
    description: 'Nothing pauses, every tool runs immediately.',
    icon: IconFlash,
    colorKey: 'warningColor',
  },
];

// Legacy add-in prefixes: the general chat used to inline the selection — or the whole
// document — into the message text. Nothing writes them any more (both add-in chats send
// the selection as `selection_content` and read the document through tools), but threads
// written before that still carry them, so the parsers below stay.
const WORD_DOCUMENT_CONTEXT_PREFIX = 'Use this Word document text as context:';
const WORD_SELECTION_CONTEXT_PREFIX = 'Use this selected Word text as context:';
// How the backend wraps `selection_content` on the persisted message — the same tag for the
// general and the templating chat, so one parser restores both.
const CURRENT_SELECTION_PATTERN = /^\s*<current_selection>\n?([\s\S]*?)\n?<\/current_selection>\s*/;

// Remembered per-org, same pattern as the chat model (useModelSelection.ts).
const CHAT_MODE_STORAGE_PREFIX = 'gaia:chat-mode:';

function chatModeStorageKey(orgUid: string): string {
  return `${CHAT_MODE_STORAGE_PREFIX}${orgUid}`;
}

function readStoredConfirmationMode(orgUid: string | undefined): ChatConfirmationMode | null {
  if (!orgUid) return null;
  try {
    const raw = localStorage.getItem(chatModeStorageKey(orgUid));
    if (isEnumValue(ChatConfirmationMode, raw)) {
      return raw;
    }
  } catch {
    // Ignore malformed/unavailable storage; fall back to the default.
  }
  return null;
}

function storeConfirmationMode(orgUid: string | undefined, mode: ChatConfirmationMode): void {
  if (!orgUid) return;
  try {
    localStorage.setItem(chatModeStorageKey(orgUid), mode);
  } catch {
    // Storage may be unavailable (private mode / quota); selection still works for the session.
  }
}

const props = withDefaults(
  defineProps<{
    showCloseButton?: boolean;
    // Embedded instances (e.g. the Templates task-pane sub-tab) fill their
    // container and skip the /gaia-chat route coupling
    embedded?: boolean;
    // Which chat backend this instance talks to. `general` is the default chat
    // (main app + Office "gaia-chat" tab); the Templates tab passes `templating`
    // to reach the template-authoring chat; the review-project Chat pane passes
    // `review_project`. Fixed for the component's lifetime.
    chatKind?: 'general' | 'templating' | 'review_project';
    // Due-diligence only: the review project whose data room the chat is scoped
    // to (sent as `review_project_uid` when starting a new thread).
    reviewProjectUid?: string;
    /**
     * Directory composer uploads land in. The review-project chat passes its data room
     * root so uploads join the room; without it uploads go to the vault root.
     */
    uploadDirectoryUid?: string;
    // Due-diligence only: the vault file revision uids from the data room that the
    // assistant should focus on for the next message (sent as `selected_revision_uids`).
    // Reactive — read at send time so the current Data Room selection always applies.
    selectedRevisionUids?: string[];
  }>(),
  {
    showCloseButton: false,
    embedded: false,
    chatKind: 'general',
    reviewProjectUid: undefined,
    uploadDirectoryUid: undefined,
    selectedRevisionUids: () => [],
  }
);

const isTemplating = computed(() => props.chatKind === 'templating');
const isReviewProject = computed(() => props.chatKind === 'review_project');

// Thread CRUD targets the general, templating, or review-project backend based on
// the chat kind.
const threadApi = computed(() => {
  switch (props.chatKind) {
    case 'templating':
      return {
        search: searchThreads2,
        getById: getThreadById2,
        update: updateThread2,
        remove: deleteThread2,
      };
    case 'review_project':
      return {
        search: (options: {
          body: LimitableSearchableQuery;
        }): RequestResult<SearchProjectThreadsResponses, SearchProjectThreadsErrors, false> =>
          searchProjectThreads({
            ...options,
            query: { review_project_uid: props.reviewProjectUid ?? '' },
          }),
        getById: getThreadById3,
        update: updateThread3,
        remove: deleteThread3,
      };
    default:
      return {
        search: searchThreads,
        getById: getThreadById,
        update: updateThread,
        remove: deleteThread,
      };
  }
});

const emit = defineEmits<{
  close: [];
  'clear-selected-revisions': [];
  /** Composer uploads that finished processing — add them to the data-room selection. */
  'add-selected-revisions': [revisionUids: string[]];
  /** An uploaded file's card was removed — take its revision out of the selection. */
  'remove-selected-revisions': [revisionUids: string[]];
  // A LangGraph human-approval decision was submitted. Fired at submit time,
  // before the resume stream settles, so hosts reconcile optimistically.
  'interrupt-decision': [decision: { interruptType: string | null; action: string }];
}>();

const gaiaStore = useGaiaStore();
const vaultStore = useVaultStore();
const reviewProjectStore = useReviewProjectStore();
const userAttentionStore = useUserAttentionStore();
const organizationStore = useOrganizationStore();
const route = useRoute();
const router = useRouter();
const messageApi = useGaiaToast();
const { openUploadDialog } = useVaultUploadDialog();
const composerFileInput = ref<HTMLInputElement | null>(null);
const isDraggingFiles = ref(false);

const input = ref('');
const messages = ref<ChatMessage[]>([]);
const viewMode = ref<'threads' | 'chat'>('chat');
const currentThreadTitle = ref('New chat');
const isStreaming = ref(false);
const threadUid = ref<string | null>(null);
const currentThread = ref<ChatThreadInDb | null>(null);
const togglePublicLoadingUid = ref<string | null>(null);
const isRestoringThread = ref(false);
const errorMessage = ref<string | null>(null);
const threadsError = ref<string | null>(null);
const threadsLoading = ref(false);
const threadLoadingUid = ref<string | null>(null);
const threadDeleteLoadingUid = ref<string | null>(null);
const threads = ref<ChatThreadInDb[]>([]);
const showThreadEditModal = ref(false);
const threadBeingEdited = ref<ChatThreadInDb | null>(null);
const editThreadTitle = ref('');
const editThreadMetadataText = ref('');
const editThreadIsArchived = ref(false);
const threadEditSaving = ref(false);
const threadEditError = ref<string | null>(null);
const messagesContainer = ref<HTMLDivElement | null>(null);
const showGoToBottomButton = ref(false);
const selectedFileKeys = ref<string[]>([]);
const selectedContextMetaByKey = ref<Record<string, SelectedContextMeta | undefined>>({});
// Only composer uploads get an entry; anything referenced from the vault or CM is
// already processed, so an absent entry means "ready".
const contextStatusByKey = ref<Record<string, SelectedContextStatus>>({});
const uploadProgressByKey = ref<Record<string, number>>({});
// Revision created by a composer upload, keyed like the chip. The review-project chat
// needs it to move a finished upload into the data-room revision selection.
const uploadedRevisionUidByKey = ref<Record<string, string>>({});
const selectedWordText = ref<string | null>(null);
const isLoadingWordSelection = ref(false);
const commentingMessageId = ref<string | null>(null);
// Sent with every stream/resume request (see getChatRequestFields) rather
// than persisted on the thread — a pure client-side preference, unlike the
// model, which is pinned to the thread when it is created.
const currentConfirmationMode = ref<ChatConfirmationMode>(
  readStoredConfirmationMode(organizationStore.activeOrganization?.uid) ?? CONFIRMATION_MODE_DEFAULT
);
const pendingContextMetadataRequests = new Set<string>();
let officeSelectionChangeTimeoutId: number | null = null;
let isOfficeSelectionHandlerRegistered = false;
let isUnmounted = false;

const {
  showModelMenu,
  modelsLoading,
  modelMenuError,
  selectingModelKey,
  availableChatModels,
  selectedModelProviderLabel,
  currentChatModelSelection,
  chatModelProviderGroups,
  filteredChatModels,
  isCurrentChatModel,
  selectModelProviderGroup,
  handleSelectChatModel,
} = useModelSelection();
/**
 * The model the open thread is pinned to. A thread is pinned when it is created,
 * so while one is open the picker is read-only and shows this. A brand-new
 * thread is pinned before its record is fetched, so until then it is the model
 * the run was started with. Null on a new chat, where the stored preference is
 * picked.
 */
const pinnedChatModel = computed(() => {
  const thread = currentThread.value;
  if (thread) return { provider: thread.llm_provider, model: thread.llm_model };
  return threadUid.value ? currentChatModelSelection.value : null;
});
const displayedChatModel = computed(() => pinnedChatModel.value ?? currentChatModelSelection.value);
const themeVars = useThemeVars();
const isThreadListView = computed(() => viewMode.value === 'threads');
const isOfficeAddin = computed(() => isOfficeAddinHost());
const hasMessages = computed(() => messages.value.length > 0);
const currentThreadIsPublic = computed(() => currentThread.value?.is_public ?? false);
const showWelcomePrompt = computed(
  () => !isThreadListView.value && !hasMessages.value && !isRestoringThread.value
);
const isTemplateSkillset = isTemplating;
const welcomeTitle = computed(() => {
  if (isReviewProject.value) return 'How can I help with your Project?';
  if (isTemplateSkillset.value) return 'How can I help with your template?';
  return 'What can I help with today?';
});
const hasPendingInterrupts = computed(() =>
  messages.value.some(message =>
    message.interrupt?.interrupts.some(interrupt => interrupt.status !== 'resolved')
  )
);
const pendingInterruptAlertTitle = computed(() => {
  const unresolved = messages.value.flatMap(
    message =>
      message.interrupt?.interrupts.filter(interrupt => interrupt.status !== 'resolved') ?? []
  );
  return unresolved.length > 0 && unresolved.every(interrupt => interrupt.shape === 'user_question')
    ? 'Answer the question above before sending another message.'
    : 'Approval required before sending another message.';
});
const activeInterruptMessageId = computed(() => {
  const message = messages.value.find(item =>
    item.interrupt?.interrupts.some(interrupt => interrupt.status === 'pending')
  );
  return message?.id ?? null;
});
const confirmationModeOptions = computed(() =>
  CONFIRMATION_MODE_OPTIONS.map(option => ({
    ...option,
    color: themeVars.value[option.colorKey],
  }))
);
const confirmationModeColor = computed(
  () =>
    confirmationModeOptions.value.find(option => option.mode === currentConfirmationMode.value)
      ?.color ?? themeVars.value.textColor3
);
const confirmationModeLabel = computed(
  () =>
    CONFIRMATION_MODE_OPTIONS.find(option => option.mode === currentConfirmationMode.value)
      ?.label ?? 'Manual'
);
const confirmationModeDescription = computed(
  () =>
    CONFIRMATION_MODE_OPTIONS.find(option => option.mode === currentConfirmationMode.value)
      ?.description ?? ''
);
const canSend = computed(
  () =>
    input.value.trim().length > 0 &&
    !isStreaming.value &&
    !hasPendingInterrupts.value &&
    pendingContextFiles.value.length === 0
);

// Shown under the composer while a reference is not yet answerable.
const composerPendingLabel = computed(() => {
  const pending = pendingContextFiles.value;
  if (pending.length === 0) return null;
  const uploading = pending.filter(contextFile => contextFile.status === 'uploading').length;
  const noun = `file${pending.length === 1 ? '' : 's'}`;
  return uploading > 0
    ? `Uploading ${uploading} ${noun}…`
    : `Processing ${pending.length} ${noun}: you can send in a moment.`;
});
const hasComposerContext = computed(
  () =>
    selectedContextFiles.value.length > 0 ||
    Boolean(selectedWordText.value) ||
    (isReviewProject.value && tableSelectedRevisionCount.value > 0)
);
const sortedThreads = computed(() =>
  [...threads.value].sort(
    (a, b) =>
      new Date(b.updated_at ?? b.created_at).getTime() -
      new Date(a.updated_at ?? a.created_at).getTime()
  )
);
const selectedContextFiles = computed(() =>
  selectedFileKeys.value.map(key => {
    const type = inferContextTypeFromKey(key);
    const meta: SelectedContextMeta | undefined = selectedContextMetaByKey.value[key];
    return {
      key,
      type,
      name: meta?.name ?? fallbackContextName(key, type),
      kind: meta?.kind ?? fallbackContextKind(key, type),
      status: contextStatusByKey.value[key] ?? 'ready',
      progress: uploadProgressByKey.value[key] ?? 0,
    };
  })
);

const pendingContextFiles = computed(() =>
  selectedContextFiles.value.filter(
    contextFile => contextFile.status === 'uploading' || contextFile.status === 'processing'
  )
);

// Tile colour by what the card is — file types get the familiar hues (red PDF,
// blue Word), non-file references their own accent. Failed cards go red so the
// broken state is visible without reading the status line.
const CONTEXT_TILE_COLORS: Record<string, string> = {
  PDF: '#ef4444',
  DOC: '#2563eb',
  DOCX: '#2563eb',
  XLS: '#16a34a',
  XLSX: '#16a34a',
};

function contextTileColor(contextFile: {
  type: SelectedContextType;
  kind: string;
  status: SelectedContextStatus;
}): string {
  if (contextFile.status === 'failed') return '#dc2626';
  if (contextFile.type === 'contract') return '#f59e0b';
  if (contextFile.type === 'gaia') return '#9333ea';
  return CONTEXT_TILE_COLORS[contextFile.kind.toUpperCase()] ?? '#6b7280';
}

// The kind label doubles as the status line while a reference is not yet answerable.
function contextStatusLabel(contextFile: {
  kind: string;
  status: SelectedContextStatus;
  progress: number;
}): string {
  if (contextFile.status === 'uploading') return `Uploading ${contextFile.progress}%`;
  if (contextFile.status === 'processing') return 'Processing…';
  if (contextFile.status === 'failed') return 'Failed';
  return contextFile.kind;
}

function setContextStatus(key: string, status: SelectedContextStatus): void {
  contextStatusByKey.value = { ...contextStatusByKey.value, [key]: status };
}

/**
 * Upload files dropped on (or picked from) the composer into a folder the user picks
 * (one upload dialog per add), then reference them for this conversation.
 *
 * Each file is tracked from `processing`; the vault websocket flips it to `ready` once
 * preprocessing has extracted its text. Uploads count against the same
 * reference cap as anything picked from the vault or contract management.
 */
async function uploadFilesToComposer(files: File[]): Promise<void> {
  if (files.length === 0) return;

  const remainingSlots = MAX_ASK_AI_ITEMS - selectedFileKeys.value.length;
  if (remainingSlots <= 0) {
    messageApi.warning(`You can reference at most ${MAX_ASK_AI_ITEMS} items at a time.`);
    return;
  }
  // Archives are the one type rejected up front: the pipeline would only park them as
  // unreadable after a full upload, and the remedy is different — unpack first. Every
  // other type uploads and lets the pipeline's verdict (ocr_status) decide readiness.
  const readable = files.filter(file => !isArchive(file.type, file.name));
  if (readable.length < files.length) {
    messageApi.warning(
      "Archives can't be attached — the AI can't read them. Unpack the archive in the vault and attach the files instead."
    );
    if (readable.length === 0) return;
  }

  const accepted = readable.slice(0, remainingSlots);
  if (accepted.length < readable.length) {
    messageApi.warning(
      `Only ${accepted.length} of ${readable.length} files were attached — the limit is ${MAX_ASK_AI_ITEMS}.`
    );
  }

  // A review project's chat stays inside its data room; cancelling attaches nothing.
  const uploaded = await openUploadDialog({
    files: accepted,
    scopeRootUid: props.uploadDirectoryUid,
    scopeRootLabel: props.uploadDirectoryUid ? 'Data room' : undefined,
    max: remainingSlots,
  });
  if (!uploaded || isUnmounted) return;

  for (const { fileUid, revision } of uploaded) {
    const key = `file-${fileUid}`;
    selectedFileKeys.value = [...selectedFileKeys.value, key];
    selectedContextMetaByKey.value = {
      ...selectedContextMetaByKey.value,
      [key]: {
        name: revision.file_name,
        kind: inferFileKind(revision.file_name, revision.mime_type),
        type: 'file',
      },
    };
    uploadedRevisionUidByKey.value = {
      ...uploadedRevisionUidByKey.value,
      [key]: revision.uid,
    };
    setContextStatus(key, 'processing');
    // Covers the window between finalize and our websocket subscription landing.
    void recheckProcessingChip(key);
  }
}

// The general chat references uploads directly; the review-project chat uploads into
// its data room (via `uploadDirectoryUid`) and converts finished uploads into the
// revision selection. Only the templating chat has no document context to attach to.
const canAttachFiles = computed(() => !isTemplating.value);

function openComposerFilePicker(): void {
  composerFileInput.value?.click();
}

function handleComposerFilesPicked(event: Event): void {
  const input = event.target as HTMLInputElement;
  void uploadFilesToComposer([...(input.files ?? [])]);
  // Reset so picking the same file twice in a row still fires a change event.
  input.value = '';
}

function handleComposerDragOver(event: DragEvent): void {
  if (!canAttachFiles.value || !event.dataTransfer?.types.includes('Files')) return;
  event.preventDefault();
  isDraggingFiles.value = true;
}

function handleComposerDragLeave(event: DragEvent): void {
  // `dragleave` fires on whichever child the pointer exits, so "left the shell" has
  // to be read off relatedTarget — comparing target to currentTarget misses exits
  // that happen over a child and leaves the overlay stuck.
  const shell = event.currentTarget as HTMLElement | null;
  const enteredElement = event.relatedTarget as Node | null;
  if (!shell || !enteredElement || !shell.contains(enteredElement)) {
    isDraggingFiles.value = false;
  }
}

function handleComposerDrop(event: DragEvent): void {
  if (!canAttachFiles.value || !event.dataTransfer?.types.includes('Files')) return;
  event.preventDefault();
  isDraggingFiles.value = false;
  void uploadFilesToComposer([...event.dataTransfer.files]);
}

/**
 * What every stream and resume request carries. The model is not part of it: a
 * thread is pinned to its model when it is created, so only a new thread sends
 * one (see getNewThreadModelFields) and a continue or a resume sends none.
 */
function getChatRequestFields(): Pick<
  GeneralChatStreamRequest,
  'confirmation_mode' | 'client_surface'
> {
  // Only the general chat's schema declares a surface — the templating and review-project
  // backends each have a fixed one. It is a capability claim ("I will answer document_tool
  // interrupts"), which is why it rides the resumes too: the tool belt is rebuilt for a
  // resumed run, so a resume claiming the web app would unbind the very live-document tool
  // whose interrupt it is answering.
  const surfaceFields =
    props.chatKind === 'general'
      ? {
          client_surface: isOfficeAddin.value
            ? ChatClientSurface.WORD_ADDIN
            : ChatClientSurface.WEB_APP,
        }
      : {};

  return { confirmation_mode: currentConfirmationMode.value, ...surfaceFields };
}

/** The picked model for a new thread; null while no model is available to start one with. */
function getNewThreadModelFields(): Pick<
  GeneralChatStreamRequest,
  'model' | 'llm_provider'
> | null {
  const selection = currentChatModelSelection.value;
  return selection ? { model: selection.model, llm_provider: selection.provider } : null;
}

type VaultSelectionRequestFields = Pick<
  GeneralChatStreamRequest,
  'selected_vault_file_uids' | 'selected_contract_uids' | 'selected_contract_file_uids'
>;

// The composer's chips, split into the three uid lists the general chat backend expects.
// Sent on every turn — the backend rebuilds the corpus from scratch each time, so a turn
// without them leaves the set-based document tools (list / grep / search) unbound. `gaia-`
// keys are GAIA CLM sections rather than referenceable items, so they never travel.
function getVaultSelectionRequestFields(): VaultSelectionRequestFields {
  const fileUids: string[] = [];
  const contractUids: string[] = [];
  const contractFileUids: string[] = [];

  for (const key of selectedFileKeys.value) {
    if (key.startsWith('file-')) fileUids.push(key.slice('file-'.length));
    else if (key.startsWith('cfile-')) contractFileUids.push(key.slice('cfile-'.length));
    else if (key.startsWith('contract-')) contractUids.push(key.slice('contract-'.length));
  }

  return {
    selected_vault_file_uids: fileUids,
    selected_contract_uids: contractUids,
    selected_contract_file_uids: contractFileUids,
  };
}

function createId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function parseWordSelectionMessage(
  content: string
): Pick<ChatMessage, 'content' | 'wordSelectionContext'> {
  const contextStart = `${WORD_SELECTION_CONTEXT_PREFIX}\n\n"""\n`;
  if (!content.startsWith(contextStart)) {
    return { content };
  }

  const rest = content.slice(contextStart.length);
  const contextEnd = rest.indexOf('\n"""');
  if (contextEnd === -1) {
    return { content };
  }

  const wordSelectionContext = rest.slice(0, contextEnd).trim();
  const userPrompt = rest.slice(contextEnd + '\n"""'.length).trim();

  if (!wordSelectionContext) {
    return { content };
  }

  return {
    content: userPrompt,
    wordSelectionContext,
  };
}

function parseWordDocumentMessage(
  content: string
): Pick<ChatMessage, 'content' | 'wordDocumentContext'> {
  const contextStart = `${WORD_DOCUMENT_CONTEXT_PREFIX}\n\n"""\n`;
  if (!content.startsWith(contextStart)) {
    return { content };
  }

  const rest = content.slice(contextStart.length);
  const contextEnd = rest.indexOf('\n"""');
  if (contextEnd === -1) {
    return { content };
  }

  const wordDocumentContext = rest.slice(0, contextEnd).trim();
  const userPrompt = rest.slice(contextEnd + '\n"""'.length).trim();

  if (!wordDocumentContext) {
    return { content };
  }

  return {
    content: userPrompt,
    wordDocumentContext,
  };
}

function parseWordContextMessage(
  content: string
): Pick<ChatMessage, 'content' | 'wordDocumentContext' | 'wordSelectionContext'> {
  const parsedSelection = parseWordSelectionMessage(content);
  if (parsedSelection.wordSelectionContext) {
    return parsedSelection;
  }
  return parseWordDocumentMessage(content);
}

function parseCurrentSelectionMessage(
  content: string
): Pick<ChatMessage, 'content' | 'wordSelectionContext'> {
  const match = content.match(CURRENT_SELECTION_PATTERN);
  if (!match) {
    return { content };
  }

  const wordSelectionContext = match[1].trim();
  if (!wordSelectionContext) {
    return { content };
  }

  return {
    content: content.slice(match[0].length),
    wordSelectionContext,
  };
}

function parseSelectedDocumentsMessage(
  content: string
): Pick<
  ChatMessage,
  | 'content'
  | 'selectedDocumentsIncluded'
  | 'selectedDocumentCount'
  | 'selectedDocumentNames'
  | 'selectedDocumentKeys'
> {
  const selectedDocumentsMatch = content.match(
    /^\s*<selected_documents>\s*([\s\S]*?)\s*<\/selected_documents>\s*/
  );

  if (!selectedDocumentsMatch) {
    return { content };
  }

  const selectedDocumentsBlock = selectedDocumentsMatch.at(1) ?? '';
  const { names, keys } = extractSelectedDocumentRefs(selectedDocumentsBlock);
  const selectedDocumentCount = selectedDocumentsBlock
    .split('\n')
    .filter(line => /^-\s+\[[^\]]+\]/.test(line.trim())).length;

  return {
    content: content.slice(selectedDocumentsMatch[0].length).trim(),
    selectedDocumentsIncluded: true,
    selectedDocumentCount: selectedDocumentCount > 0 ? selectedDocumentCount : undefined,
    selectedDocumentNames: names.length > 0 ? names : undefined,
    selectedDocumentKeys: keys.length > 0 ? keys : undefined,
  };
}

// The general chat's line grammar — a cross-repo contract with the backend's
// `_compose_message`: one `- [marker] {uid}` line per item, no names (the backend
// deliberately doesn't resolve them). The review project's block differs: its marker
// IS the revision uid and its content is a bare file name.
const GENERAL_SELECTION_MARKER_PREFIXES: Record<string, string> = {
  file: 'file-',
  contract: 'contract-',
  'contract file': 'cfile-',
};

interface SelectedDocumentRefs {
  /** Display names carried in the block itself (review-project grammar). */
  names: string[];
  /** Context keys for uid-only lines (general grammar) — names resolve separately. */
  keys: string[];
}

function extractSelectedDocumentRefs(selectedDocumentsBlock: string): SelectedDocumentRefs {
  const names: string[] = [];
  const keys: string[] = [];
  let nextContentLineIsFileName = false;

  for (const rawLine of selectedDocumentsBlock.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;

    const markerMatch = line.match(/^-\s+\[([^\]]+)\]\s*(.*)$/);
    if (markerMatch) {
      const marker = markerMatch.at(1)?.trim() ?? '';
      const content = markerMatch.at(2)?.trim() ?? '';
      const keyPrefix = GENERAL_SELECTION_MARKER_PREFIXES[marker];
      if (keyPrefix) {
        // The content is a raw uid — never a display name. It must not reach the UI;
        // the name is resolved from the uid like the composer chips do.
        if (content) keys.push(`${keyPrefix}${content}`);
        nextContentLineIsFileName = false;
      } else if (content) {
        names.push(content);
        nextContentLineIsFileName = false;
      } else {
        nextContentLineIsFileName = true;
      }
      continue;
    }

    if (nextContentLineIsFileName) {
      names.push(line);
      nextContentLineIsFileName = false;
    }
  }

  return { names, keys };
}

function parseUserMessage(
  content: string
): Pick<
  ChatMessage,
  | 'content'
  | 'wordDocumentContext'
  | 'wordSelectionContext'
  | 'selectedDocumentsIncluded'
  | 'selectedDocumentCount'
  | 'selectedDocumentNames'
  | 'selectedDocumentKeys'
> {
  const parsedWordContext = parseWordContextMessage(content);
  // Both the review project and the general chat prepend `<selected_documents>`, so a
  // restored thread has to strip it back out of the user's bubble either way.
  const parsedSelectedDocuments = parseSelectedDocumentsMessage(parsedWordContext.content);
  // Stripped last: the backend writes it after the `<selected_documents>` block. It only
  // reports a selection when it found one, so a legacy message keeps the one its prefix
  // carried.
  const parsedCurrentSelection = parseCurrentSelectionMessage(parsedSelectedDocuments.content);

  return {
    ...parsedWordContext,
    ...parsedSelectedDocuments,
    ...parsedCurrentSelection,
  };
}

function selectedDocumentsSummary(message: ChatMessage): string {
  if (message.selectedDocumentCount) {
    return `${message.selectedDocumentCount} selected document${
      message.selectedDocumentCount === 1 ? '' : 's'
    } included`;
  }
  return 'Selected documents included';
}

function removeSelectedWordText(): void {
  selectedWordText.value = null;
}

function getWordApi(): WordApi | null {
  return (window as unknown as { Word?: WordApi }).Word ?? null;
}

function ensureWordCommentApiAvailable(): WordApi {
  const wordApi = getWordApi();
  if (!wordApi) {
    throw new Error('Word comment insertion is not available in this host.');
  }

  const isSupported = window.Office?.context?.requirements?.isSetSupported?.('WordApi', '1.4');
  if (isSupported === false) {
    throw new Error('Adding Word comments requires WordApi 1.4 support.');
  }

  return wordApi;
}

function isOfficeResultSucceeded(status: unknown): boolean {
  return status === window.Office?.AsyncResultStatus?.Succeeded || status === 'succeeded';
}

function getOfficeSelectionChangedEventType(): unknown {
  return window.Office?.EventType?.DocumentSelectionChanged ?? 'documentSelectionChanged';
}

function readWordSelection(): Promise<string> {
  const document = window.Office?.context?.document;
  const textCoercionType = window.Office?.CoercionType?.Text ?? 'text';

  if (!document?.getSelectedDataAsync) {
    return Promise.reject(new Error('Word selection is not available in this host.'));
  }

  return new Promise<string>((resolve, reject) => {
    document.getSelectedDataAsync(textCoercionType, (result: OfficeSelectedTextResult) => {
      if (isOfficeResultSucceeded(result.status)) {
        resolve(result.value ?? '');
        return;
      }

      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      reject(new Error(result.error?.message || 'Failed to read the selected Word text.'));
    });
  });
}

async function syncSelectedWordTextFromDocument(): Promise<void> {
  if (!isOfficeAddin.value || isLoadingWordSelection.value) return;

  isLoadingWordSelection.value = true;
  try {
    const rawSelectedText = await readWordSelection();
    const selectedText = rawSelectedText.trim();
    selectedWordText.value = selectedText || null;
  } catch (error) {
    logger.warn('Failed to sync Word selection:', error);
  } finally {
    isLoadingWordSelection.value = false;
  }
}

function handleOfficeSelectionChanged(): void {
  if (officeSelectionChangeTimeoutId !== null) {
    window.clearTimeout(officeSelectionChangeTimeoutId);
  }

  officeSelectionChangeTimeoutId = window.setTimeout(() => {
    officeSelectionChangeTimeoutId = null;
    void syncSelectedWordTextFromDocument();
  }, 150);
}

function addOfficeSelectionChangedHandler(): void {
  const document = window.Office?.context?.document;
  if (!isOfficeAddin.value || !document?.addHandlerAsync || isOfficeSelectionHandlerRegistered) {
    return;
  }

  document.addHandlerAsync(
    getOfficeSelectionChangedEventType(),
    handleOfficeSelectionChanged,
    (result: OfficeVoidResult) => {
      if (isOfficeResultSucceeded(result.status)) {
        isOfficeSelectionHandlerRegistered = true;
        void syncSelectedWordTextFromDocument();
        return;
      }

      logger.warn(
        'Failed to register Word selection listener:',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        result.error?.message || 'Unknown Office error'
      );
    }
  );
}

function removeOfficeSelectionChangedHandler(): void {
  const document = window.Office?.context?.document;
  if (!isOfficeSelectionHandlerRegistered || !document?.removeHandlerAsync) {
    return;
  }

  document.removeHandlerAsync(getOfficeSelectionChangedEventType(), {
    handler: handleOfficeSelectionChanged,
  });
  isOfficeSelectionHandlerRegistered = false;
}

async function addAssistantResponseAsWordComment(message: ChatMessage): Promise<void> {
  if (!isOfficeAddin.value || message.role !== 'assistant' || commentingMessageId.value) return;

  const commentText = message.content.trim();
  if (!commentText) {
    messageApi.warning('There is no response text to add as a comment.');
    return;
  }

  commentingMessageId.value = message.id;
  try {
    const wordApi = ensureWordCommentApiAvailable();

    await wordApi.run(async context => {
      const selection = context.document.getSelection();
      selection.load('text');
      await context.sync();

      if (!selection.text?.trim()) {
        throw new Error('Select a paragraph in Word first.');
      }

      if (!selection.insertComment) {
        throw new Error('Word comment insertion is not available for this selection.');
      }

      selection.insertComment(commentText);
      await context.sync();
    });

    messageApi.success('Added response as a Word comment.');
  } catch (error) {
    logger.error('Failed to add Word comment:', error);
    messageApi.error(error instanceof Error ? error.message : 'Failed to add Word comment.');
  } finally {
    commentingMessageId.value = null;
  }
}

// ── Per-code-block actions (copy / insert) ──────────────────────────────
// A template-mode reply can carry several fenced ```jinja blocks (one per
// snippet), so the copy/insert affordances live on each block (ChatGPT-style)
// rather than once per message. MarkdownRenderer renders the buttons and hands
// each handler that block's raw text.

async function copyCodeBlock(code: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(code);
    messageApi.success('Copied to the clipboard.');
  } catch (error) {
    logger.error('Failed to copy:', error);
    messageApi.error('Failed to copy.');
  }
}

async function insertCodeBlock(code: string): Promise<void> {
  if (!isOfficeAddin.value) return;
  try {
    await insertTextAtSelection(code);
    messageApi.success('Inserted at the cursor.');
  } catch (error) {
    logger.error('Failed to insert snippet:', error);
    messageApi.error(error instanceof Error ? error.message : 'Failed to insert the snippet.');
  }
}

// Copy on every assistant code block; Insert only in the Word add-in's template
// mode (where dropping Jinja into the document makes sense).
const chatCodeActions = computed<MarkdownCodeAction[]>(() => {
  const actions: MarkdownCodeAction[] = [{ key: 'copy', label: 'Copy', handler: copyCodeBlock }];
  if (isOfficeAddin.value && isTemplating.value) {
    actions.push({ key: 'insert', label: 'Insert at cursor', handler: insertCodeBlock });
  }
  return actions;
});

let scrollToBottomRafId: number | null = null;
function scrollToBottom(): void {
  if (scrollToBottomRafId !== null) return;
  scrollToBottomRafId = requestAnimationFrame(() => {
    scrollToBottomRafId = null;
    const container = messagesContainer.value;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
    showGoToBottomButton.value = false;
  });
}

let scrollVisibilityRafId: number | null = null;
function updateGoToBottomButtonVisibility(): void {
  if (scrollVisibilityRafId !== null) return;
  scrollVisibilityRafId = requestAnimationFrame(() => {
    scrollVisibilityRafId = null;
    const container = messagesContainer.value;
    if (!container || isThreadListView.value || !hasMessages.value) {
      showGoToBottomButton.value = false;
      return;
    }

    const maxScrollable = container.scrollHeight - container.clientHeight;
    if (maxScrollable <= 0) {
      showGoToBottomButton.value = false;
      return;
    }

    const distanceFromBottom = maxScrollable - container.scrollTop;
    const hiddenRatio = distanceFromBottom / maxScrollable;
    showGoToBottomButton.value = hiddenRatio > 0.05;
  });
}

function mergeContent(current: string, incoming: string): string {
  if (!current) return incoming;
  if (incoming.startsWith(current)) return incoming;
  if (current.startsWith(incoming)) return current;
  return current + incoming;
}

function extractThreadUid(payload: unknown): string | null {
  return (
    getString(payload, ['thread_uid', 'threadUid', 'thread_id', 'threadId']) ??
    getString(toRecord(payload)?.data, ['thread_uid', 'threadUid', 'thread_id', 'threadId']) ??
    getString(toRecord(payload)?.message, ['thread_uid', 'threadUid', 'thread_id', 'threadId'])
  );
}

function extractDelta(payload: unknown): { delta: string | null; role: string | null } {
  const candidates = [payload, toRecord(payload)?.data, toRecord(payload)?.message];
  for (const candidate of candidates) {
    const delta =
      getString(candidate, ['delta', 'token', 'content', 'message', 'text', 'value']) ?? null;
    const role = getString(candidate, ['role']) ?? null;
    if (delta) return { delta, role };
  }
  return { delta: null, role: null };
}

function toQueryString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim().length > 0) return value;
  if (Array.isArray(value)) {
    const first = value.find(item => typeof item === 'string' && item.trim().length > 0);
    return typeof first === 'string' ? first : null;
  }
  return null;
}

function getRouteThreadUid(): string | null {
  return toQueryString(route.query.thread_uid);
}

async function syncThreadUidToRoute(uid: string | null): Promise<void> {
  if (props.showCloseButton || props.embedded || route.name !== 'gaia-chat') return;

  const currentUid = getRouteThreadUid();
  if ((uid ?? null) === (currentUid ?? null)) return;

  const nextQuery = { ...route.query } as Record<string, string | null>;
  if (uid) {
    nextQuery.thread_uid = uid;
    delete nextQuery.new_chat;
    delete nextQuery.thread_list;
  } else {
    delete nextQuery.thread_uid;
  }

  await router.replace({ query: nextQuery });
}

function normalizePendingInterruptPayload(pendingInterrupt: unknown): unknown {
  const parsed = parseJsonIfString(pendingInterrupt);
  if (!parsed) return null;

  if (Array.isArray(parsed)) {
    return {
      type: 'interrupt',
      interrupts: parsed,
    };
  }

  const record = toRecord(parsed);
  if (!record) return null;

  const interrupts = record.interrupts;
  if (Array.isArray(interrupts)) {
    return {
      ...record,
      type: getString(record, ['type']) ?? 'interrupt',
      interrupts,
    };
  }

  if (toRecord(record.value) && getString(record, ['id'])) {
    return {
      type: 'interrupt',
      interrupts: [record],
    };
  }

  const nestedInterrupt = toRecord(record.interrupt);
  if (nestedInterrupt && toRecord(nestedInterrupt.value) && getString(nestedInterrupt, ['id'])) {
    return {
      type: 'interrupt',
      interrupts: [nestedInterrupt],
    };
  }

  return null;
}

function extractPendingInterruptPayload(threadData: ChatThreadWithMessages): unknown {
  const threadRecord = toRecord(threadData);
  if (!threadRecord) return null;

  const directPendingInterrupt = normalizePendingInterruptPayload(threadRecord.pending_interrupt);
  if (directPendingInterrupt) return directPendingInterrupt;

  const metadataRecord = toRecord(threadRecord.thread_metadata);
  if (!metadataRecord) return null;

  return normalizePendingInterruptPayload(metadataRecord.pending_interrupt);
}

function applyInterruptPayload(payload: unknown, assistantIndex: number): void {
  const interrupts = normalizeInterruptPayload(payload);
  if (interrupts.length === 0) return;

  const assistantMessage = messages.value.at(assistantIndex);
  // Only used when the run interrupted before streaming any prose of its own.
  const fallbackContent = interrupts.some(item => item.shape === 'user_question')
    ? 'I have a question before I continue.'
    : 'I need your approval before I can run the requested tool action.';
  const interruptMessage: ChatMessage = {
    id: assistantMessage?.id ?? createId(),
    role: 'assistant',
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    content: assistantMessage?.content || fallbackContent,
    interrupt: { interrupts },
  };

  if (assistantMessage) {
    messages.value[assistantIndex] = interruptMessage;
  } else {
    messages.value.push(interruptMessage);
  }
  scrollToBottom();
}

function applyStreamChunk(payload: unknown, assistantIndex: number): void {
  if (payload === null || payload === undefined) return;

  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (trimmed === '[DONE]' || trimmed === 'DONE') return;
    const assistantMessage = messages.value.at(assistantIndex);
    if (assistantMessage) {
      assistantMessage.content = mergeContent(assistantMessage.content, payload);
      scrollToBottom();
    }
    return;
  }

  if (Array.isArray(payload)) {
    for (const item of payload) applyStreamChunk(item, assistantIndex);
    return;
  }

  const payloadType = getString(payload, ['type']);
  if (payloadType === 'interrupt') {
    applyInterruptPayload(payload, assistantIndex);
    return;
  }

  if (payloadType === 'thread_info') {
    const incomingThreadUid = getString(payload, ['thread_uid', 'threadUid']);
    if (incomingThreadUid) {
      threadUid.value = incomingThreadUid;
      void refreshCurrentThread(incomingThreadUid);
    }
    const title = getString(payload, ['title']);
    if (title) currentThreadTitle.value = title;
    return;
  }

  if (payloadType === 'token') {
    const token = getString(payload, ['content', 'token', 'text']);
    if (!token) return;
    const assistantMessage = messages.value.at(assistantIndex);
    if (assistantMessage) {
      assistantMessage.content = mergeContent(assistantMessage.content, token);
      scrollToBottom();
    }
    return;
  }

  if (payloadType === 'done') return;

  const thread = extractThreadUid(payload);
  if (thread) threadUid.value = thread;

  const { delta, role } = extractDelta(payload);
  if (!delta) return;
  if (role && role !== 'assistant') return;

  const assistantMessage = messages.value.at(assistantIndex);
  if (assistantMessage) {
    assistantMessage.content = mergeContent(assistantMessage.content, delta);
    scrollToBottom();
  }
}

function resetStreamingState(): void {
  isStreaming.value = false;
  pendingDocumentTool = null;
  docToolStatus.value = null;
  statusGroups.clear();
}

/** Kill the run: the stop button, and only the stop button. */
function stopStreaming(): void {
  chatStream.cancelChat();
  resetStreamingState();
}

/**
 * Stop following the run but leave it executing on the worker. Runs live in
 * Celery now, so `chat_cancel` is an explicit kill — unmounting, switching
 * threads or opening the thread list must detach instead. The run finishes on its
 * own and flags its thread for attention.
 */
function detachStreaming(): void {
  chatStream.detach();
  resetStreamingState();
}

/**
 * Refetching the thread once a run settles is what clears its server-side
 * attention flag for a user who watched it finish. The transcript needs no
 * repair on this path — we saw every token — so only the thread itself is
 * refreshed, not the messages.
 */
function noteRunSettled(): void {
  const uid = threadUid.value;
  if (!uid || isUnmounted) return;
  clearThreadAttention(uid);
  void refreshCurrentThread(uid);
}

async function refreshCurrentThread(uid: string): Promise<void> {
  try {
    const response = await threadApi.value.getById({ path: { thread_uid: uid } });
    if (response.data && threadUid.value === uid) {
      currentThread.value = response.data;
    }
  } catch (error) {
    logger.error('Failed to load thread details:', error);
  }
}

async function resetChat(): Promise<void> {
  detachStreaming();
  closeThreadEditModal();
  messages.value = [];
  showGoToBottomButton.value = false;
  threadUid.value = null;
  currentThread.value = null;
  currentThreadTitle.value = 'New chat';
  viewMode.value = 'chat';
  errorMessage.value = null;
  selectedFileKeys.value = [];
  selectedContextMetaByKey.value = {};
  announcedRevisionUids.value = new Set();
  selectedWordText.value = null;
  await fetchThreads();
}

async function openThreadList(): Promise<void> {
  detachStreaming();
  closeThreadEditModal();
  showGoToBottomButton.value = false;
  viewMode.value = 'threads';
  await fetchThreads();
}

function toggleThreadList(): void {
  if (isThreadListView.value) {
    viewMode.value = 'chat';
    return;
  }
  void openThreadList();
}

// `cfile-` is checked before `contract-` would ever match — the two prefixes are distinct
// so ordering is not load-bearing, but keep them apart if either is ever renamed.
function inferContextTypeFromKey(key: string): SelectedContextType {
  if (key.startsWith('cfile-')) return 'contractFile';
  if (key.startsWith('contract-')) return 'contract';
  if (key.startsWith('gaia-')) return 'gaia';
  return 'file';
}

function fallbackContextName(key: string, type: SelectedContextType): string {
  const uid = key.includes('-') ? key.slice(key.indexOf('-') + 1) : key;
  if (type === 'contract') return `Contract ${uid.slice(0, 8)}`;
  if (type === 'contractFile') return `Contract file ${uid.slice(0, 8)}`;
  if (type === 'gaia') return `GAIA file ${uid.slice(0, 8)}`;
  return `File ${uid.slice(0, 8)}`;
}

function fallbackContextKind(key: string, type: SelectedContextType): string {
  if (type === 'contract') return 'Contract';
  if (type === 'contractFile') return 'Contract file';
  if (type === 'gaia') return 'GAIA';
  const extension = key.split('.').pop();
  if (extension && extension !== key) return extension.toUpperCase();
  return 'File';
}

function inferFileKind(fileName: string, mimeType?: string | null): string {
  const extension = fileName.includes('.') ? fileName.split('.').pop() : null;
  if (extension) return extension.toUpperCase();
  if (mimeType && mimeType.includes('/')) {
    const subtype = mimeType.split('/')[1];
    if (subtype) return subtype.toUpperCase();
  }
  return 'File';
}

function setContextMetadata(key: string, metadata: SelectedContextMeta): void {
  selectedContextMetaByKey.value = {
    ...selectedContextMetaByKey.value,
    [key]: metadata,
  };
}

/**
 * Look up a context key's display name and kind. `name` is null when nothing
 * human-readable exists — callers decide whether to fall back or omit.
 */
/**
 * For vault files, `ocrStatus` reports whether the pipeline has made the file
 * readable — the chip status derives from it, so unreadable references are never
 * silently presented as ready. `undefined` for types with no such notion.
 */
async function fetchContextMetadata(key: string): Promise<{
  name: string | null;
  kind: string;
  ocrStatus?: PreprocessingTaskStatus | null;
}> {
  const type = inferContextTypeFromKey(key);
  const uid = key.includes('-') ? key.slice(key.indexOf('-') + 1) : key;

  if (type === 'contract') {
    const response = await getContractManagementContract({ path: { uid } });
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
    return { name: response.data?.name.trim() || null, kind: 'Contract' };
  }

  if (type === 'contractFile') {
    const response = await getContractManagementContractFile({ path: { uid } });
    const contractFile = response.data;
    return {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      name: contractFile?.vault_file?.current_revision?.file_name.trim() || null,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      kind: contractFile?.type || 'Contract file',
    };
  }

  if (type === 'gaia') {
    const gaiaFile = gaiaStore.getGaiaFileById(uid);
    return { name: gaiaFile?.title ?? null, kind: 'GAIA' };
  }

  const cachedVaultFile = vaultStore.getItemByKey(key);
  if (cachedVaultFile?.name) {
    return {
      name: cachedVaultFile.name,
      kind: inferFileKind(cachedVaultFile.name, cachedVaultFile.contentType),
      ocrStatus: cachedVaultFile.ocrStatus,
    };
  }

  const response = await getVaultFileMetadata({ path: { file_uid: uid } });
  const vaultFile = response.data;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
  const fileName = vaultFile?.current_revision?.file_name.trim() || null;
  return {
    name: fileName,
    kind: inferFileKind(fileName ?? '', vaultFile?.current_revision?.mime_type),
    ocrStatus: vaultFile?.current_revision?.ocr_status,
  };
}

async function ensureContextMetadataForKey(key: string): Promise<void> {
  if (key in selectedContextMetaByKey.value || pendingContextMetadataRequests.has(key)) return;
  pendingContextMetadataRequests.add(key);

  const type = inferContextTypeFromKey(key);
  try {
    const { name, kind, ocrStatus } = await fetchContextMetadata(key);
    if (isUnmounted) return;
    setContextMetadata(key, { name: name ?? fallbackContextName(key, type), kind, type });

    // The pipeline verdict decides readiness for every file reference, however it was
    // added (vault "Ask AI", a shared URL) — not just composer uploads. Uploads manage
    // their own status ('uploading' precedes this fetch), so an existing entry wins.
    if (
      type === 'file' &&
      !(key in contextStatusByKey.value) &&
      ocrStatus !== PreprocessingTaskStatus.DONE
    ) {
      setContextStatus(key, 'processing');
      // Terminal verdicts (FAILED / ARCHIVE) surface immediately; a genuinely pending
      // file stays 'processing' and resolves via the vault websocket subscription.
      applyOcrStatusToChip(key, ocrStatus);
    }
  } catch (error) {
    logger.warn(`Failed to resolve selected file metadata for ${key}:`, error);
    setContextMetadata(key, {
      name: fallbackContextName(key, type),
      kind: fallbackContextKind(key, type),
      type,
    });
  } finally {
    pendingContextMetadataRequests.delete(key);
  }
}

// Names for `selectedDocumentKeys` on restored messages. Kept apart from
// `selectedContextMetaByKey`, which `syncSelectedContextMetadata` prunes to the
// composer's current keys — message names must outlive composer changes. A key that
// cannot be resolved to a real name is stored as absent and never rendered: the
// count chip already covers it, and a raw uid must not reach the UI.
const messageDocumentNamesByKey = ref<Record<string, string>>({});
const pendingMessageDocumentNames = new Set<string>();

async function ensureMessageDocumentName(key: string): Promise<void> {
  if (messageDocumentNamesByKey.value[key] || pendingMessageDocumentNames.has(key)) return;
  pendingMessageDocumentNames.add(key);
  try {
    const { name } = await fetchContextMetadata(key);
    if (isUnmounted || !name) return;
    messageDocumentNamesByKey.value = { ...messageDocumentNamesByKey.value, [key]: name };
  } catch (error) {
    logger.warn(`Failed to resolve selected document name for ${key}:`, error);
  } finally {
    pendingMessageDocumentNames.delete(key);
  }
}

// `thread_metadata.referenced_items` → context keys. The kind vocabulary is shared
// with the `<selected_documents>` markers (the backend keeps them in lockstep).
// Parsed defensively: the metadata is untyped JSONB that also carries other keys
// (e.g. pending_interrupt), and threads from before this feature have no entry.
const REFERENCED_KIND_PREFIXES: Record<string, string> = {
  file: 'file-',
  contract: 'contract-',
  'contract file': 'cfile-',
};

function threadReferencedKeys(thread: ChatThreadInDb | null): string[] {
  const raw = thread?.thread_metadata.referenced_items;
  if (!Array.isArray(raw)) return [];

  const keys: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const { kind, uid } = item as Record<string, unknown>;
    if (typeof kind !== 'string' || typeof uid !== 'string' || !uid) continue;
    const prefix = REFERENCED_KIND_PREFIXES[kind];
    if (!prefix) continue;
    const key = `${prefix}${uid}`;
    if (!seen.has(key)) {
      seen.add(key);
      keys.push(key);
    }
  }
  return keys;
}

const currentThreadReferencedKeys = computed(() => threadReferencedKeys(currentThread.value));

// Resolution starts when the popover opens, not when the thread loads — most visits
// never open it, and each uid is one metadata request.
function handleReferencedFilesPopoverShow(show: boolean): void {
  if (!show) return;
  for (const key of currentThreadReferencedKeys.value) {
    void ensureMessageDocumentName(key);
  }
}

// Resolved name, or a kind label while loading / when the item is gone or unreadable.
// Never the uid.
function referencedItemLabel(key: string): string {
  return (
    messageDocumentNamesByKey.value[key] ?? fallbackContextKind(key, inferContextTypeFromKey(key))
  );
}

// Each kind's home page. Vault files land on the versions view — the file's page in
// the main vault; contracts and contract files on their CM detail views.
function referencedItemRoute(key: string): string {
  const type = inferContextTypeFromKey(key);
  const uid = key.slice(key.indexOf('-') + 1);
  if (type === 'contract') return `/contract-management/contracts/${uid}`;
  if (type === 'contractFile') return `/contract-management/contract-files/${uid}`;
  return `/vault/file/${uid}/versions`;
}

interface MessageReferencedEntry {
  /** Context key when the reference carries a uid; null for the review project's
   * name-only lines, which have nothing to navigate to. */
  key: string | null;
  type: SelectedContextType;
  name: string;
  kind: string;
}

/** A message's references as typed rows: uid-carrying entries (general chat) are
 * navigable and show their resolved names; name-only entries (review project) are
 * display-only. */
function messageReferencedEntries(message: ChatMessage): MessageReferencedEntry[] {
  return [
    ...(message.selectedDocumentKeys ?? []).map(key => {
      const type = inferContextTypeFromKey(key);
      return { key, type, name: referencedItemLabel(key), kind: fallbackContextKind(key, type) };
    }),
    ...(message.selectedDocumentNames ?? []).map(name => ({
      key: null,
      type: 'file' as const,
      name,
      kind: 'File',
    })),
  ];
}

async function syncSelectedContextMetadata(keys: string[]): Promise<void> {
  const keySet = new Set(keys);
  const pruneToSelection = <T,>(record: Record<string, T>): Record<string, T> =>
    Object.fromEntries(Object.entries(record).filter(([key]) => keySet.has(key)));

  selectedContextMetaByKey.value = pruneToSelection(selectedContextMetaByKey.value);
  // Statuses must not outlive their chips: a stale `processing` entry would block
  // sending forever if the same file were ever referenced again (its OCR event is
  // long gone by then).
  contextStatusByKey.value = pruneToSelection(contextStatusByKey.value);
  uploadProgressByKey.value = pruneToSelection(uploadProgressByKey.value);
  uploadedRevisionUidByKey.value = pruneToSelection(uploadedRevisionUidByKey.value);

  await Promise.all(keys.map(key => ensureContextMetadataForKey(key)));
}

function removeSelectedContext(key: string): void {
  // A ready upload card in the review chat stands for a selected revision — removing
  // the card must deselect it, or the next message would still focus on the file.
  const uploadedRevisionUid = uploadedRevisionUidByKey.value[key];
  if (isReviewProject.value && uploadedRevisionUid) {
    emit('remove-selected-revisions', [uploadedRevisionUid]);
  }
  selectedFileKeys.value = selectedFileKeys.value.filter(selectedKey => selectedKey !== key);
}

// Revisions represented by an upload card, so the count chip doesn't double-count them.
const uploadCardRevisionUids = computed(() => {
  const uids = new Set<string>();
  for (const key of selectedFileKeys.value) {
    const uid = uploadedRevisionUidByKey.value[key];
    if (uid) uids.add(uid);
  }
  return uids;
});

// Data-room table picks without a card of their own — what the count chip shows.
// Revisions already shown on a sent message this session. The data-room selection
// persists across sends (it is the table's checkbox state), but the composer clears
// like the general chat: once a message has displayed the references, the count chip
// stops re-announcing them. Newly ticked files show up again until the next send.
const announcedRevisionUids = ref<Set<string>>(new Set());

const tableSelectedRevisionCount = computed(
  () =>
    props.selectedRevisionUids.filter(
      uid => !uploadCardRevisionUids.value.has(uid) && !announcedRevisionUids.value.has(uid)
    ).length
);

// Clearing the selection clears everything it contains — including ready upload
// cards, whose revisions are part of it. In-flight uploads stay: they aren't
// selected yet and cancelling them is the card's own ✕.
function clearSelectedRevisions(): void {
  selectedFileKeys.value = selectedFileKeys.value.filter(
    key => !uploadedRevisionUidByKey.value[key] || contextStatusByKey.value[key] !== 'ready'
  );
  emit('clear-selected-revisions');
}

function buildTitle(message: string): string {
  const normalized = message.trim();
  if (normalized.length <= 60) return normalized;
  return `${normalized.slice(0, 57)}...`;
}

function summarizeHitlSubmission(
  interrupt: NormalizedInterrupt,
  payload: HitlSubmitPayload
): string {
  // A question's reply is the user's answer, not a verdict on the agent's plan —
  // so the echoed bubble reads as one: the option's own label, or their own words.
  if (payload.shape === 'user_question') {
    const { resumeValue } = payload;
    if (resumeValue.choice) {
      const choice = interrupt.requests[0]?.choices.find(item => item.key === resumeValue.choice);
      return choice?.label ?? resumeValue.choice;
    }
    return resumeValue.message ?? '';
  }

  if (payload.shape === 'tool_confirmation') {
    const { resumeValue } = payload;
    if (resumeValue.choice) return `Decision: ${resumeValue.choice}`;
    return `Clarification: ${resumeValue.message ?? ''}`;
  }
  const summary = payload.decisions
    .map(decision => decision.type + (decision.message ? ` (${decision.message})` : ''))
    .join(', ');
  return `Decision: ${summary}`;
}

let currentAssistantIndex: number | null = null;

// ── Document tools (the model pulling document context on demand) ───────────
// A "document_tool" interrupt is NOT a human-approval interrupt: we run the
// requested Office.js read and resume the agent with the result, no UI panel.
// We record the request on arrival and execute it AFTER the current run settles
// (mirroring the deleted auto-apply), so we never resume mid-run.
//
// A single `convert_document` turn fires a SEQUENCE of these round-trips
// (get_chunks → apply_edits → validate → fix-loop), so the budget must cover a
// normal conversion with a few fix passes — not just one tool call. It's a
// safety net against a runaway loop, not a per-conversion limit; the backend
// bounds its own fix loop. Very large docs that exceed it convert partially
// (the known future batch-pipeline case).
const DOCUMENT_TOOL_LOOP_BUDGET = 20;
interface PendingDocumentTool {
  interruptId: string;
  tool: string;
  args: Record<string, unknown>;
  /** Optional human-friendly status authored by the backend (overrides the FE phrase). */
  label?: string;
}
let pendingDocumentTool: PendingDocumentTool | null = null;
// Transient "what I'm doing" status shown during a document-tool round-trip.
const docToolStatus = ref<string | null>(null);

function clipStatusLabel(text: string, max = 40): string {
  const normalized = text.replace(/\s+/g, ' ').trim();
  return normalized.length > max ? `${normalized.slice(0, max)}…` : normalized;
}

/**
 * A friendly, specific "what I'm doing now" line for a document-tool round-trip,
 * derived from the tool + its args (e.g. the search query, the section name, the
 * number of edits). The backend can override this with its own `label`.
 */
function describeDocumentTool(tool: string, args: Record<string, unknown>): string {
  switch (tool) {
    case 'search': {
      const query = typeof args.query === 'string' ? args.query.trim() : '';
      return query ? `Searching for “${clipStatusLabel(query)}”…` : 'Searching the document…';
    }
    case 'get_outline':
      return 'Reading the document outline…';
    case 'get_section': {
      const heading = typeof args.heading === 'string' ? args.heading.trim() : '';
      return heading ? `Reading the “${clipStatusLabel(heading)}” section…` : 'Reading a section…';
    }
    case 'get_chunks': {
      const index = typeof args.index === 'number' ? args.index : null;
      return index === null ? 'Reading the document…' : `Reading the document (part ${index + 1})…`;
    }
    case 'apply_edits': {
      const count = Array.isArray(args.edits) ? args.edits.length : 0;
      return count > 0
        ? `Applying ${count} change${count === 1 ? '' : 's'} to the document…`
        : 'Applying changes to the document…';
    }
    case 'validate':
      return 'Checking the document…';
    case 'list_fields':
      return 'Reading the document’s fields…';
    default:
      return 'Working in the document…';
  }
}

function parseDocumentToolInterrupt(payload: unknown): PendingDocumentTool | null {
  const record = toRecord(payload);
  const interrupts = Array.isArray(record?.interrupts) ? record.interrupts : [];
  for (const raw of interrupts) {
    const item = toRecord(raw);
    if (!item) continue;
    const value = toRecord(item.value);
    if (!value || getString(value, ['type']) !== 'document_tool') continue;
    const tool = getString(value, ['tool']);
    if (!tool) continue;
    return {
      interruptId: getString(item, ['id']) ?? '',
      tool,
      args: toRecord(value.args) ?? {},
      label: getString(value, ['label', 'status']) ?? undefined,
    };
  }
  return null;
}

// Live "what the agent is doing" groups streamed as chat_status frames during a
// run (see frontend.md / PY-346). Ephemeral: cleared on run start/interrupt/stop.
const statusGroups = useChatStatusGroups();

const chatStream = useChatStream({
  kind: props.chatKind,
  onStatus: update => {
    statusGroups.handleStatus(update);
  },
  onToken: text => {
    if (currentAssistantIndex === null) return;
    // The agent is answering now — drop the "doing X" status (a tool resume
    // that produces no tokens keeps its status until the next tool replaces it).
    if (docToolStatus.value) docToolStatus.value = null;
    applyStreamChunk({ type: 'token', content: text }, currentAssistantIndex);
  },
  onThreadInfo: info => {
    if (currentAssistantIndex === null) return;
    applyStreamChunk(info, currentAssistantIndex);
  },
  onInterrupt: interrupt => {
    if (currentAssistantIndex === null) return;
    // An interrupt implicitly closes all open status groups — the backend never
    // sends group_end frames for them; a resume opens fresh groups instead.
    statusGroups.clear();
    // A document-tool request is fulfilled by us (run the Office.js read +
    // resume), not by the user — record it and let the post-run driver handle
    // it. Everything else is a human-approval interrupt → existing panel path.
    const documentTool = parseDocumentToolInterrupt(interrupt);
    if (documentTool) {
      pendingDocumentTool = documentTool;
      return;
    }
    applyStreamChunk(interrupt, currentAssistantIndex);
  },
  onError: (_level, message) => {
    errorMessage.value = message;
  },
});

/**
 * After a run completes, fulfill any document-tool request it raised — run the
 * Office.js read and resume the agent with the result, streaming the
 * continuation into the SAME assistant bubble (`assistantIndex`). The resume
 * may raise another tool request, so loop until none remain or the per-turn
 * budget is exhausted. Aborts if the thread changed underneath (New chat).
 */
async function drivePendingDocumentTool(assistantIndex: number): Promise<void> {
  let budget = DOCUMENT_TOOL_LOOP_BUDGET;
  try {
    while (pendingDocumentTool) {
      const request = pendingDocumentTool;
      pendingDocumentTool = null;
      const overBudget = budget <= 0;
      budget -= 1;

      const threadAtStart = threadUid.value;
      let resumeValue: { ok: boolean; result?: unknown; error?: string };
      if (overBudget) {
        // Hit the safety cap — resume with an error so the backend tool returns
        // cleanly (rather than leaving the graph paused), then stop driving.
        resumeValue = {
          ok: false,
          error: 'Document lookup limit reached for this turn. Summarize what you have and stop.',
        };
      } else {
        // Show "what I'm doing" and KEEP it through the (fast) Office.js read
        // AND the (slow) LLM resume that follows. It's cleared when the agent
        // starts answering (onToken) or replaced by the next tool's status —
        // NOT right after the read, which would leave "Thinking…" covering the
        // multi-second wait the user actually experiences.
        docToolStatus.value = request.label ?? describeDocumentTool(request.tool, request.args);
        try {
          resumeValue = { ok: true, result: await executeDocumentTool(request.tool, request.args) };
        } catch (error) {
          logger.error('Document tool failed:', error);
          resumeValue = {
            ok: false,
            error: error instanceof Error ? error.message : 'Failed to run the document tool.',
          };
        }
      }

      // The conversation moved on while we were reading (New chat / switched
      // thread) — drop the resume so nothing leaks into another conversation.
      if (!threadUid.value || threadUid.value !== threadAtStart) {
        pendingDocumentTool = null;
        return;
      }

      const resumeRequest: ChatResumeRequestBody = {
        thread_uid: threadUid.value,
        interrupt_id: request.interruptId,
        resume_value: resumeValue,
        ...getChatRequestFields(),
      };
      await startStream(resumeRequest, assistantIndex);

      if (overBudget) {
        // Already told the agent to stop; don't keep chasing further tool calls.
        pendingDocumentTool = null;
        messageApi.warning('The assistant made many document lookups; stopping this turn.');
        return;
      }
    }
  } finally {
    // Whatever path we exit by (done, stale-thread, over-budget, throw), the
    // round-trip is over — drop the status so it never sticks.
    docToolStatus.value = null;
  }
}

async function startStream(
  request: ChatStreamRequestBody | ChatResumeRequestBody,
  assistantIndex: number
): Promise<void> {
  currentAssistantIndex = assistantIndex;
  // Every run (fresh or resume) starts with a clean status panel — a resumed
  // tool re-announces itself with fresh group ids.
  statusGroups.clear();
  try {
    if ('message' in request) {
      await chatStream.startChat(request);
    } else {
      await chatStream.resumeChat(request);
    }
  } finally {
    currentAssistantIndex = null;
    statusGroups.clear();
  }
}

async function sendMessage(): Promise<void> {
  if (!canSend.value) return;

  // Only a new thread needs a model; an existing one is already pinned to its own.
  const modelFields = threadUid.value ? {} : getNewThreadModelFields();
  if (!modelFields) {
    messageApi.error('No chat model is available yet — try again in a moment.');
    return;
  }

  const text = input.value.trim();
  const isTemplateMode = isTemplating.value;
  // Both add-in chats work the same way now: the document body never travels — the
  // assistant reads the live document on demand through its document tools (search /
  // get_outline / get_section / get_chunks) — and only the current selection is pushed,
  // as `selection_content`, because no tool can fetch it. Only the add-in has a document
  // to select in, and the backend rejects the field from any other surface.
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
  const selectionContent = isOfficeAddin.value ? selectedWordText.value?.trim() || null : null;

  input.value = '';
  selectedWordText.value = null;
  errorMessage.value = null;
  viewMode.value = 'chat';
  if (!threadUid.value) {
    currentThreadTitle.value = buildTitle(text);
  }

  const userMessage: ChatMessage = {
    id: createId(),
    role: 'user',
    content: text,
    wordSelectionContext: selectionContent ?? undefined,
  };
  messages.value.push(userMessage);

  const assistantMessage: ChatMessage = {
    id: createId(),
    role: 'assistant',
    content: '',
  };
  messages.value.push(assistantMessage);
  const assistantIndex = messages.value.length - 1;

  scrollToBottom();

  const baseRequest = {
    message: text,
    thread_uid: threadUid.value ?? null,
    title: threadUid.value ? null : buildTitle(text),
    ...getChatRequestFields(),
    ...modelFields,
  };
  // The templating and general requests carry the add-in's current selection; the
  // review-project request scopes the chat to a review project's data room (project uid
  // only on a new thread) and includes the currently selected data-room file revisions; the
  // general request also carries the composer's vault selection, which *is* the scope of
  // its set-based document tools.
  let request: ChatStreamRequestBody;
  if (isTemplateMode) {
    request = {
      ...baseRequest,
      selection_content: selectionContent,
    };
  } else if (isReviewProject.value) {
    const revisionUids = props.selectedRevisionUids;
    request = {
      ...baseRequest,
      review_project_uid: threadUid.value ? null : (props.reviewProjectUid ?? null),
      selected_revision_uids: revisionUids,
    };

    // Mirror the general chat: the references move onto the message (name-only
    // entries, like the block the backend writes) and the composer indication
    // clears — while the data-room checkboxes, which ARE the selection, stay put.
    if (revisionUids.length > 0) {
      const names = revisionUids
        .map(uid => {
          const uploadKey = Object.keys(uploadedRevisionUidByKey.value).find(
            key => uploadedRevisionUidByKey.value[key] === uid
          );
          if (!uploadKey) return reviewProjectStore.dataRoomFileNameByRevisionUid[uid];
          const uploadMeta: SelectedContextMeta | undefined =
            selectedContextMetaByKey.value[uploadKey];
          return uploadMeta?.name;
        })
        .filter((name): name is string => Boolean(name));

      userMessage.selectedDocumentsIncluded = true;
      userMessage.selectedDocumentCount = revisionUids.length;
      userMessage.selectedDocumentNames = names.length > 0 ? names : undefined;

      announcedRevisionUids.value = new Set([...announcedRevisionUids.value, ...revisionUids]);
      // Upload cards are announced too — they leave the composer like general-chat chips.
      selectedFileKeys.value = [];
    }
  } else {
    const vaultSelection = getVaultSelectionRequestFields();
    request = {
      ...baseRequest,
      ...vaultSelection,
      selection_content: selectionContent,
    };

    // The chips are consumed by the send: the message now carries the references
    // (mirroring what the backend's <selected_documents> block will show on reload),
    // and the composer clears for the next question. Follow-ups don't need the uids
    // re-sent — they ride the thread history, and the assistant's tools permission-
    // check per call.
    const referencedKeys = [
      ...(vaultSelection.selected_vault_file_uids ?? []).map(uid => `file-${uid}`),
      ...(vaultSelection.selected_contract_uids ?? []).map(uid => `contract-${uid}`),
      ...(vaultSelection.selected_contract_file_uids ?? []).map(uid => `cfile-${uid}`),
    ];
    if (referencedKeys.length > 0) {
      userMessage.selectedDocumentsIncluded = true;
      userMessage.selectedDocumentCount = referencedKeys.length;
      userMessage.selectedDocumentKeys = referencedKeys;
      // Seed the message-name cache from the chips so the cards render instantly;
      // a chip that only ever had its fallback name gets a proper fetch instead.
      for (const key of referencedKeys) {
        const keyMeta: SelectedContextMeta | undefined = selectedContextMetaByKey.value[key];
        const metaName = keyMeta?.name;
        if (metaName && metaName !== fallbackContextName(key, inferContextTypeFromKey(key))) {
          messageDocumentNamesByKey.value = {
            ...messageDocumentNamesByKey.value,
            [key]: metaName,
          };
        } else {
          void ensureMessageDocumentName(key);
        }
      }
      selectedFileKeys.value = [];
    }
  }

  isStreaming.value = true;
  if (threadUid.value) clearThreadAttention(threadUid.value);

  try {
    await startStream(request, assistantIndex);
    // Fulfill any document-tool request this run raised (and chain through the
    // resulting resumes) once the run has settled.
    await drivePendingDocumentTool(assistantIndex);
  } catch (error) {
    if (error instanceof ChatStreamCancelledError) {
      // User-initiated cancel — keep whatever tokens already arrived.
    } else {
      logger.error('Failed to stream GAIA chat:', error);
      // A `chat_error` frame carries the backend's user-facing text (e.g. the provider
      // stayed rate-limited through its retries); anything else gets our copy.
      const serverMessage = error instanceof ChatStreamServerError ? error.message : null;
      errorMessage.value = error instanceof Error ? error.message : 'Failed to send message';
      assistantMessage.content =
        serverMessage ?? 'Sorry, I could not complete that request. Please try again.';
      assistantMessage.failed = true;
      messageApi.error(serverMessage ?? 'Failed to send message');
    }
  } finally {
    isStreaming.value = false;
    pendingDocumentTool = null;
    noteRunSettled();
    if (isThreadListView.value) {
      await fetchThreads();
    }
  }
}

async function doStreamResume(
  interrupt: NormalizedInterrupt,
  summaryContent: string,
  streamFn: (assistantIndex: number) => Promise<void>,
  errorLabel: string
): Promise<void> {
  interrupt.status = 'submitting';
  interrupt.errorMessage = null;
  errorMessage.value = null;

  const reviewSummary: ChatMessage = {
    id: createId(),
    role: 'user',
    content: summaryContent,
  };
  messages.value.push(reviewSummary);

  const assistantMessage: ChatMessage = {
    id: createId(),
    role: 'assistant',
    content: '',
  };
  messages.value.push(assistantMessage);
  const assistantIndex = messages.value.length - 1;
  scrollToBottom();

  isStreaming.value = true;
  if (threadUid.value) clearThreadAttention(threadUid.value);

  try {
    await streamFn(assistantIndex);
    // A human-approved turn can also lead the model to pull document context.
    await drivePendingDocumentTool(assistantIndex);
    interrupt.status = 'resolved';
  } catch (error) {
    if (error instanceof ChatStreamCancelledError) {
      interrupt.status = 'pending';
    } else {
      logger.error(`Failed to submit ${errorLabel}:`, error);
      const serverMessage = error instanceof ChatStreamServerError ? error.message : null;
      const reason = error instanceof Error ? error.message : `Failed to submit ${errorLabel}`;
      errorMessage.value = reason;
      assistantMessage.content =
        serverMessage ?? 'Sorry, I could not apply that decision. Please try again.';
      assistantMessage.failed = true;
      interrupt.status = 'pending';
      interrupt.errorMessage = reason;
      messageApi.error(serverMessage ?? `Failed to submit ${errorLabel}`);
    }
  } finally {
    isStreaming.value = false;
    pendingDocumentTool = null;
    noteRunSettled();
    if (isThreadListView.value) {
      await fetchThreads();
    }
  }
}

async function submitHitlDecision(
  interrupt: NormalizedInterrupt,
  payload: HitlSubmitPayload
): Promise<void> {
  if (isStreaming.value || interrupt.status !== 'pending') return;

  const currentThreadUid = threadUid.value;
  if (!currentThreadUid) {
    interrupt.errorMessage = 'Thread ID missing. Start a new chat message first.';
    return;
  }

  // Fired optimistically, before the resume stream settles, so hosts (e.g. the
  // review-project pane) can reconcile immediately — see ReviewProjectDetailView.
  if (payload.shape === 'langgraph_action') {
    emit('interrupt-decision', {
      interruptType: interrupt.businessType,
      action: payload.decisions[0]?.type ?? '',
    });
  }

  await doStreamResume(
    interrupt,
    summarizeHitlSubmission(interrupt, payload),
    async assistantIndex => {
      const resumeRequest: ChatResumeRequestBody = {
        thread_uid: currentThreadUid,
        interrupt_id: interrupt.interruptId,
        ...(payload.shape === 'tool_confirmation' || payload.shape === 'user_question'
          ? { resume_value: payload.resumeValue }
          : { decisions: payload.decisions }),
        ...getChatRequestFields(),
      };
      await startStream(resumeRequest, assistantIndex);
    },
    interrupt.shape === 'user_question' ? 'answer' : 'interrupt decision'
  );
}

onBeforeUnmount(() => {
  isUnmounted = true;
  userAttentionStore.setActiveThread(null);
  detachStreaming();
  disposeSocketStatusListener();
  removeOfficeSelectionChangedHandler();
  if (officeSelectionChangeTimeoutId !== null) {
    window.clearTimeout(officeSelectionChangeTimeoutId);
    officeSelectionChangeTimeoutId = null;
  }
  if (scrollToBottomRafId !== null) cancelAnimationFrame(scrollToBottomRafId);
  if (scrollVisibilityRafId !== null) cancelAnimationFrame(scrollVisibilityRafId);
});

// Vault items handed over by "Ask AI" in the file vault, as composer context keys.
// The `selectedFileKeys` watcher resolves their display names for the chips.
function seedSelectedContextFromRoute(): void {
  const parseUids = (value: unknown): string[] =>
    typeof value === 'string'
      ? value
          .split(',')
          .map(uid => uid.trim())
          .filter(Boolean)
      : [];

  const keys = [
    ...parseUids(route.query.vault_files).map(uid => `file-${uid}`),
    ...parseUids(route.query.contracts).map(uid => `contract-${uid}`),
    ...parseUids(route.query.contract_files).map(uid => `cfile-${uid}`),
  ];
  // The entry points already enforce the cap; re-apply it here so a hand-built URL
  // can't load the composer with an unbounded reference list.
  if (keys.length > 0) selectedFileKeys.value = keys.slice(0, MAX_ASK_AI_ITEMS);
}

async function initializeChatView(): Promise<void> {
  const usesRoute = !props.showCloseButton && !props.embedded;
  if (usesRoute && 'thread_list' in route.query) {
    await openThreadList();
    return;
  }

  if (usesRoute && 'new_chat' in route.query) {
    await resetChat();
    seedSelectedContextFromRoute();
    return;
  }

  const requestedThreadUid = usesRoute ? getRouteThreadUid() : null;
  if (requestedThreadUid) {
    isRestoringThread.value = true;
    try {
      await Promise.all([fetchThreads(), loadThread(requestedThreadUid)]);
    } finally {
      isRestoringThread.value = false;
    }
    return;
  }

  await fetchThreads();
}

onMounted(() => {
  addOfficeSelectionChangedHandler();
  void initializeChatView();
});

watch(
  (): LocationQueryValue | LocationQueryValue[] | undefined => route.query.new_chat,
  newValue => {
    if (!props.showCloseButton && !props.embedded && newValue !== undefined) {
      void resetChat().then(() => {
        seedSelectedContextFromRoute();
      });
    }
  }
);

// The sidebar sends users here instead of into a new chat while a thread is
// waiting on them, so the badge they clicked leads to the threads themselves.
watch(
  (): LocationQueryValue | LocationQueryValue[] | undefined => route.query.thread_list,
  newValue => {
    if (!props.showCloseButton && !props.embedded && newValue !== undefined) {
      void openThreadList();
    }
  }
);

watch(
  selectedFileKeys,
  keys => {
    void syncSelectedContextMetadata(keys);
  },
  { immediate: true }
);

// Tell the store which thread is on screen so a run settling on it badges
// nothing. The thread list counts as "not looking at it": there the dot is what
// points at the thread worth reopening.
watch(
  [threadUid, isThreadListView],
  ([uid, listView]) => {
    userAttentionStore.setActiveThread(listView ? null : uid);
  },
  { immediate: true }
);

// Uids of composer uploads still waiting on preprocessing. Subscribing only to these
// keeps the chat off the vault's event firehose once everything is ready.
const processingFileUids = computed(() =>
  selectedFileKeys.value
    .filter(key => key.startsWith('file-') && contextStatusByKey.value[key] === 'processing')
    .map(key => key.slice('file-'.length))
);

// Move a `processing` chip to its terminal state. ARCHIVE is terminal too: the
// pipeline never OCRs an archive, so leaving it `processing` would block sending
// forever. Returns whether a transition happened.
function applyOcrStatusToChip(
  key: string,
  ocrStatus: PreprocessingTaskStatus | null | undefined
): boolean {
  if (contextStatusByKey.value[key] !== 'processing') return false;

  const keyMeta: SelectedContextMeta | undefined = selectedContextMetaByKey.value[key];
  const displayName = keyMeta?.name ?? 'A file';
  if (ocrStatus === PreprocessingTaskStatus.DONE) {
    setContextStatus(key, 'ready');
    // In the review-project chat the finished upload joins the revision selection but
    // keeps its card — name and type beat an anonymous "+1" on the count chip, which
    // covers only revisions picked in the data-room table.
    const uploadedRevisionUid = uploadedRevisionUidByKey.value[key];
    if (isReviewProject.value && uploadedRevisionUid) {
      emit('add-selected-revisions', [uploadedRevisionUid]);
    }
    return true;
  }
  if (ocrStatus === PreprocessingTaskStatus.FAILED) {
    setContextStatus(key, 'failed');
    messageApi.error(`${displayName} could not be processed.`);
    return true;
  }
  if (ocrStatus === PreprocessingTaskStatus.ARCHIVE) {
    setContextStatus(key, 'failed');
    messageApi.warning(
      `${displayName} is an archive — the AI can't read it. Unpack it in the vault and attach the files instead.`
    );
    return true;
  }
  return false;
}

// The websocket subscription starts only after the upload returns, so a fast
// pipeline (or one that already ran) can finish before we listen. One direct
// status check closes that gap.
async function recheckProcessingChip(key: string): Promise<void> {
  const uid = key.slice('file-'.length);
  try {
    const response = await getVaultFileMetadata({ path: { file_uid: uid } });
    if (isUnmounted) return;
    applyOcrStatusToChip(key, response.data?.current_revision?.ocr_status);
  } catch (error) {
    logger.warn(`Failed to recheck processing status for ${key}:`, error);
  }
}

useVaultWebSocket({
  fileUids: () => processingFileUids.value,
  onFileUpdated: (uid, fileData) => {
    const vaultFile = fileData as unknown as VaultFileInDb;
    applyOcrStatusToChip(`file-${uid}`, vaultFile.current_revision?.ocr_status);
  },
});

watch(
  [isThreadListView, hasMessages],
  () => {
    void nextTick(() => {
      updateGoToBottomButtonVisibility();
    });
  },
  { immediate: true }
);

watch(
  threadUid,
  uid => {
    void syncThreadUidToRoute(uid);
  },
  { flush: 'post' }
);

const formattedMessages = computed(() =>
  messages.value.map(message => ({
    ...message,
    label: message.role === 'user' ? 'You' : 'GAIA',
  }))
);

// Status groups shown in the streaming assistant bubble: all open groups, padded
// up to 5 with the most recent finished ones (older finished groups fade away).
// Once answer tokens arrive, only still-open groups remain (a parallel tool may
// still be running).
const MAX_VISIBLE_STATUS_GROUPS = 5;
const visibleStatusGroups = computed(() => {
  const streamingContent = messages.value.at(-1)?.content ?? '';
  const openGroups = statusGroups.openGroups.value;
  if (streamingContent.length > 0) return openGroups;
  const doneSlots = Math.max(0, MAX_VISIBLE_STATUS_GROUPS - openGroups.length);
  const doneGroups = statusGroups.groups.value.filter(group => group.done);
  const visibleDoneIds = new Set(
    (doneSlots > 0 ? doneGroups.slice(-doneSlots) : []).map(group => group.id)
  );
  return statusGroups.groups.value.filter(group => !group.done || visibleDoneIds.has(group.id));
});

function formatThreadDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function threadTimestamp(thread: ChatThreadInDb): string {
  return thread.updated_at ?? thread.created_at;
}

/** The model the thread is pinned to — set when it was created, never changed. */
function threadModel(thread: ChatThreadInDb): string {
  return formatModelLabel(thread.llm_model, thread.llm_provider);
}

/** "Google (EU) / Gemini 3.5 Flash" — the provider rides in the row's tooltip. */
function threadModelTitle(thread: ChatThreadInDb): string {
  const provider = formatModelProviderLabel(thread.llm_provider, thread.llm_model);
  return provider ? `${provider} / ${threadModel(thread)}` : threadModel(thread);
}

function threadTitle(thread: ChatThreadInDb): string {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
  return thread.title?.trim() || 'Untitled thread';
}

const ATTENTION_LABELS: Record<ChatAttentionReason, string> = {
  [ChatAttentionReason.INTERRUPT]: 'Waiting for your answer',
  [ChatAttentionReason.DONE]: 'Finished while you were away',
  [ChatAttentionReason.FAILED]: 'This run failed',
};

/** Why this thread is waiting for the user, from the user attention list. */
function threadAttentionLabel(thread: ChatThreadInDb): string | null {
  // Whatever the thread on screen is waiting to show, the user is already
  // looking at it — the flag only matters for threads they left behind.
  if (thread.uid === threadUid.value && !isThreadListView.value) {
    return null;
  }
  const reason = userAttentionStore.reasonFor(thread.uid);
  return reason ? ATTENTION_LABELS[reason] : null;
}

/**
 * Whether any thread this view lists is waiting on the user — the thread list is
 * one click away behind the history button, so the button carries a dot too.
 */
const hasThreadAttention = computed(() =>
  threads.value.some(thread => threadAttentionLabel(thread) !== null)
);

/** The backend clears the flag when the owner opens a thread or starts its next run. */
function clearThreadAttention(uid: string): void {
  userAttentionStore.clearThread(uid);
}

function handleThreadItemClick(thread: ChatThreadInDb): void {
  if (
    threadLoadingUid.value === thread.uid ||
    threadEditSaving.value ||
    threadDeleteLoadingUid.value !== null
  ) {
    return;
  }
  void loadThread(thread.uid);
}

function openThreadEditModal(thread: ChatThreadInDb): void {
  if (threadDeleteLoadingUid.value) return;

  threadBeingEdited.value = thread;
  editThreadTitle.value = thread.title?.trim() ?? '';
  editThreadIsArchived.value = thread.is_archived;

  const metadata = thread.thread_metadata;
  editThreadMetadataText.value =
    Object.keys(metadata).length > 0 ? JSON.stringify(metadata, null, 2) : '';

  threadEditError.value = null;
  showThreadEditModal.value = true;
}

function closeThreadEditModal(): void {
  if (threadEditSaving.value) return;
  showThreadEditModal.value = false;
  threadBeingEdited.value = null;
  threadEditError.value = null;
}

async function saveThreadEdits(): Promise<void> {
  if (!threadBeingEdited.value) return;

  const title = editThreadTitle.value.trim();
  if (!title) {
    threadEditError.value = 'Thread title is required.';
    return;
  }

  let threadMetadata: Record<string, unknown> | null = null;
  const metadataInput = editThreadMetadataText.value.trim();

  if (metadataInput.length > 0) {
    let parsedMetadata: unknown;
    try {
      parsedMetadata = JSON.parse(metadataInput);
    } catch {
      threadEditError.value = 'Thread metadata must be valid JSON.';
      return;
    }

    const metadataRecord = toRecord(parsedMetadata);
    if (!metadataRecord) {
      threadEditError.value = 'Thread metadata must be a JSON object.';
      return;
    }
    threadMetadata = metadataRecord;
  }

  threadEditSaving.value = true;
  threadEditError.value = null;

  try {
    const updateBody: ChatThreadUpdate = {
      title,
      thread_metadata: threadMetadata,
      is_archived: editThreadIsArchived.value,
    };

    const response = await threadApi.value.update({
      path: { thread_uid: threadBeingEdited.value.uid },
      body: updateBody,
    });

    const updatedThread = response.data;
    if (updatedThread) {
      threads.value = threads.value.map(thread =>
        thread.uid === updatedThread.uid ? updatedThread : thread
      );
      if (threadUid.value === updatedThread.uid) {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        currentThreadTitle.value = updatedThread.title?.trim() || 'Untitled thread';
      }
    }

    showThreadEditModal.value = false;
    threadBeingEdited.value = null;
    await fetchThreads();
    messageApi.success('Thread updated successfully');
  } catch (error) {
    logger.error('Failed to update thread:', error);
    threadEditError.value = error instanceof Error ? error.message : 'Failed to update thread';
    messageApi.error('Failed to update thread');
  } finally {
    threadEditSaving.value = false;
  }
}

async function deleteThreadByUid(thread: ChatThreadInDb): Promise<void> {
  if (threadDeleteLoadingUid.value || threadEditSaving.value) return;

  threadDeleteLoadingUid.value = thread.uid;
  threadEditError.value = null;

  try {
    await threadApi.value.remove({
      path: { thread_uid: thread.uid },
    });

    if (threadBeingEdited.value?.uid === thread.uid) {
      showThreadEditModal.value = false;
      threadBeingEdited.value = null;
    }

    threads.value = threads.value.filter(item => item.uid !== thread.uid);
    // No thread left to draw attention to.
    userAttentionStore.clearThread(thread.uid);

    if (threadUid.value === thread.uid) {
      // The thread is gone, so its run has nothing to write to — kill it.
      stopStreaming();
      messages.value = [];
      threadUid.value = null;
      currentThread.value = null;
      currentThreadTitle.value = 'New chat';
      errorMessage.value = null;
      showGoToBottomButton.value = false;
      selectedWordText.value = null;
    }

    await fetchThreads();
    messageApi.success('Thread deleted successfully');
  } catch (error) {
    logger.error('Failed to delete thread:', error);
    messageApi.error('Failed to delete thread');
  } finally {
    threadDeleteLoadingUid.value = null;
  }
}

async function toggleThreadPublic(
  thread: ChatThreadInDb | null = currentThread.value
): Promise<void> {
  if (!thread || togglePublicLoadingUid.value) return;

  togglePublicLoadingUid.value = thread.uid;
  try {
    const newIsPublic = !thread.is_public;
    const response = await threadApi.value.update({
      path: { thread_uid: thread.uid },
      body: { is_public: newIsPublic } satisfies ChatThreadUpdate,
    });

    if (response.data) {
      threads.value = threads.value.map(t => (t.uid === response.data.uid ? response.data : t));
      if (currentThread.value?.uid === response.data.uid) {
        currentThread.value = response.data;
      }
      messageApi.success(`Thread is now ${newIsPublic ? 'public' : 'private'}`);
    }
  } catch (error) {
    logger.error('Failed to toggle thread public status:', error);
    messageApi.error('Failed to update thread');
  } finally {
    togglePublicLoadingUid.value = null;
  }
}

// Sent with every subsequent stream/resume request (see getChatRequestFields) —
// purely a client-side preference, so there's nothing to await or block on.
function handleSelectConfirmationMode(mode: ChatConfirmationMode): void {
  if (mode === currentConfirmationMode.value) return;
  currentConfirmationMode.value = mode;
  storeConfirmationMode(organizationStore.activeOrganization?.uid, mode);
  const label = CONFIRMATION_MODE_OPTIONS.find(option => option.mode === mode)?.label ?? mode;
  messageApi.success(`Chat mode set to ${label}`);
}

watch(
  () => organizationStore.activeOrganization?.uid,
  orgUid => {
    const stored = readStoredConfirmationMode(orgUid);
    if (stored) currentConfirmationMode.value = stored;
  }
);

function mapThreadMessages(messagesFromApi: ChatThreadWithMessages['messages']): ChatMessage[] {
  return messagesFromApi
    .filter(msg => typeof msg.content === 'string' && msg.content.trim().length > 0)
    .map((msg, index) => {
      const role = (msg.role || '').toLowerCase();
      const normalizedRole: ChatRole = role === 'user' ? 'user' : 'assistant';
      const parsedMessage =
        normalizedRole === 'user' ? parseUserMessage(msg.content) : { content: msg.content };
      if ('selectedDocumentKeys' in parsedMessage) {
        for (const key of parsedMessage.selectedDocumentKeys ?? []) {
          void ensureMessageDocumentName(key);
        }
      }
      return {
        id: `thread-msg-${index}-${normalizedRole}`,
        role: normalizedRole,
        ...parsedMessage,
      };
    });
}

async function fetchThreads(): Promise<void> {
  threadsLoading.value = true;
  threadsError.value = null;
  try {
    const response = await threadApi.value.search({
      body: {},
    });

    if (!response.data) {
      throw new Error('Failed to load chat threads');
    }

    threads.value = response.data.results;
  } catch (error) {
    logger.error('Failed to fetch chat threads:', error);
    threadsError.value = error instanceof Error ? error.message : 'Failed to load chat threads';
  } finally {
    threadsLoading.value = false;
  }
}

/**
 * Render a fetched thread's transcript. Split out of `loadThread` so the refetch
 * that follows an attached run can refresh the messages without redoing the
 * thread-switch side effects (composer reset, view mode, reattach).
 */
function applyThreadTranscript(thread: ChatThreadWithMessages): void {
  threadUid.value = thread.uid;
  currentThread.value = thread;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
  currentThreadTitle.value = thread.title?.trim() || 'Untitled thread';
  messages.value = mapThreadMessages(thread.messages);

  const pendingInterruptPayload = extractPendingInterruptPayload(thread);
  if (pendingInterruptPayload) {
    const lastAssistantIndex = [...messages.value]
      .map(message => message.role)
      .lastIndexOf('assistant');
    const assistantIndex = lastAssistantIndex === -1 ? messages.value.length : lastAssistantIndex;
    applyInterruptPayload(pendingInterruptPayload, assistantIndex);
  }
}

/**
 * Rejoin a run that is still executing on the worker — started here before a
 * reload, from another tab, or left behind by navigating away. Frames resume from
 * now, with no token replay, so the transcript is refetched once it settles.
 */
function attachToActiveRun(thread: ChatThreadWithMessages): void {
  const activeRun = thread.active_run;
  if (!activeRun) return;
  if (activeRun.status !== TaskStatus.PENDING && activeRun.status !== TaskStatus.RUNNING) return;

  const assistantMessage: ChatMessage = { id: createId(), role: 'assistant', content: '' };
  messages.value.push(assistantMessage);
  const assistantIndex = messages.value.length - 1;
  currentAssistantIndex = assistantIndex;
  statusGroups.clear();
  isStreaming.value = true;
  scrollToBottom();

  void followActiveRun(activeRun.run_id, thread.uid, assistantIndex);
}

async function followActiveRun(
  runId: string,
  attachedThreadUid: string,
  assistantIndex: number
): Promise<void> {
  let detached = false;
  try {
    await chatStream.attachRun(runId);
    await drivePendingDocumentTool(assistantIndex);
  } catch (error) {
    if (error instanceof ChatStreamCancelledError) {
      // Unmount or thread switch — the run lives on, and whatever detached it
      // owns the view now. Don't refetch on top of it.
      detached = true;
    } else {
      logger.error('Failed to follow the running chat:', error);
      errorMessage.value =
        error instanceof Error ? error.message : 'Lost the connection to the running chat';
    }
  } finally {
    if (currentAssistantIndex === assistantIndex) currentAssistantIndex = null;
    if (!detached && !isUnmounted && threadUid.value === attachedThreadUid) {
      resetStreamingState();
      await refreshThreadAfterRun(attachedThreadUid);
    }
  }
}

/**
 * The run's frames do replay on reattach, but only while the server still holds
 * the run — so the thread GET remains what makes the transcript authoritative
 * after a run we did not watch from the start (it also covers the window where a
 * reload lands before `thread_info`, leaving us no run_id to rejoin with). The
 * GET also clears the thread's server-side attention flag.
 */
async function refreshThreadAfterRun(uid: string): Promise<void> {
  if (isUnmounted) return;
  try {
    const response = await threadApi.value.getById({ path: { thread_uid: uid } });
    if (!response.data || threadUid.value !== uid) return;
    applyThreadTranscript(response.data);
    clearThreadAttention(uid);
    scrollToBottom();
  } catch (error) {
    logger.error('Failed to refresh thread after run:', error);
  }
}

/**
 * The socket came back. A run survives it dying, so the worker may still be
 * streaming a thread we lost mid-answer — refetch and rejoin.
 */
async function reattachAfterReconnect(uid: string): Promise<void> {
  try {
    const response = await threadApi.value.getById({ path: { thread_uid: uid } });
    if (!response.data?.active_run) return;
    if (threadUid.value !== uid || isStreaming.value || isUnmounted) return;
    applyThreadTranscript(response.data);
    attachToActiveRun(response.data);
  } catch (error) {
    logger.error('Failed to rejoin the running chat after reconnect:', error);
  }
}

const disposeSocketStatusListener = getWsClient().addStatusListener(status => {
  if (status !== 'open') return;
  const uid = threadUid.value;
  if (!uid || isStreaming.value || isUnmounted) return;
  void reattachAfterReconnect(uid);
});

async function loadThread(uid: string): Promise<void> {
  if (threadLoadingUid.value === uid) return;
  // Switching threads leaves any in-flight run to the worker rather than killing
  // it — it will flag its own thread when it settles.
  detachStreaming();
  threadLoadingUid.value = uid;
  errorMessage.value = null;
  try {
    const response = await threadApi.value.getById({ path: { thread_uid: uid } });
    if (!response.data) {
      throw new Error('Failed to load thread');
    }

    // A different thread is a fresh composer view: the current selection hasn't
    // been shown on any of ITS messages yet.
    announcedRevisionUids.value = new Set();
    selectedWordText.value = null;
    applyThreadTranscript(response.data);
    // Opening a thread is what clears its flag server-side; mirror that locally.
    clearThreadAttention(uid);

    viewMode.value = 'chat';
    scrollToBottom();
    attachToActiveRun(response.data);
  } catch (error) {
    logger.error('Failed to load thread:', error);
    messageApi.error('Failed to load thread');
  } finally {
    threadLoadingUid.value = null;
  }
}
</script>

<template>
  <div
    class="gaia-chat-view"
    :class="{ 'gaia-chat-view-sidebar': props.showCloseButton || props.embedded }"
  >
    <div class="chat-toolbar">
      <div class="toolbar-left">
        <NText v-if="isThreadListView" class="toolbar-title">Threads</NText>
        <NText v-else-if="hasMessages" class="toolbar-title">{{ currentThreadTitle }}</NText>
      </div>
      <div class="toolbar-right">
        <n-badge :show="hasThreadAttention" dot type="error" :offset="[-5, 5]">
          <NButton
            quaternary
            circle
            data-tour="chat.history"
            :aria-label="hasThreadAttention ? 'Threads — one needs attention' : 'Threads'"
            @click="toggleThreadList"
          >
            <template #icon>
              <NIcon><IconHistory /></NIcon>
            </template>
          </NButton>
        </n-badge>
        <!-- Only once the thread has used files: an empty paperclip would say
             nothing, and in the Word task pane's narrow header it would only
             take room. Review threads deliberately record no referenced_items
             (their assistant works the whole data room), so they never show it. -->
        <n-popover
          v-if="!isThreadListView && !isReviewProject && currentThreadReferencedKeys.length > 0"
          trigger="click"
          placement="bottom-end"
          :show-arrow="false"
          @update:show="handleReferencedFilesPopoverShow"
        >
          <template #trigger>
            <n-tooltip placement="top" trigger="hover">
              <template #trigger>
                <span class="thread-files-trigger">
                  <n-button quaternary circle aria-label="Files used in this thread">
                    <template #icon>
                      <n-icon><paper-clip-outlined /></n-icon>
                    </template>
                  </n-button>
                </span>
              </template>
              {{ currentThreadReferencedKeys.length }} file{{
                currentThreadReferencedKeys.length === 1 ? '' : 's'
              }}
              used in this thread
            </n-tooltip>
          </template>

          <n-text depth="3" class="referenced-files-title">Used in this thread</n-text>
          <n-scrollbar class="referenced-files-list">
            <router-link
              v-for="key in currentThreadReferencedKeys"
              :key="key"
              :to="referencedItemRoute(key)"
              class="referenced-files-row"
              :aria-label="`Open ${referencedItemLabel(key)}`"
            >
              <n-icon :size="16" aria-hidden="true">
                <file-protect-outlined v-if="inferContextTypeFromKey(key) === 'contract'" />
                <file-text-outlined v-else />
              </n-icon>
              <n-ellipsis class="referenced-files-name">{{ referencedItemLabel(key) }}</n-ellipsis>
              <span class="referenced-files-kind">
                {{ fallbackContextKind(key, inferContextTypeFromKey(key)) }}
              </span>
            </router-link>
          </n-scrollbar>
        </n-popover>
        <NTooltip v-if="currentThread && !isThreadListView && isReviewProject">
          <template #trigger>
            <NButton
              quaternary
              circle
              :loading="togglePublicLoadingUid === currentThread?.uid"
              @click="toggleThreadPublic()"
            >
              <template #icon>
                <NIcon>
                  <IconUnlock v-if="currentThreadIsPublic" />
                  <IconLock v-else />
                </NIcon>
              </template>
            </NButton>
          </template>
          <div v-if="currentThreadIsPublic" style="max-width: 300px">
            This thread is public. Other users in this review project can access and interact with
            it.
          </div>
          <div v-else style="max-width: 300px">
            This thread is private. Only you can access it. Click to make it public.
          </div>
        </NTooltip>
        <NButton size="small" class="new-chat-button" data-tour="chat.new-chat" @click="resetChat">
          New chat
        </NButton>
        <NButton v-if="props.showCloseButton" quaternary circle @click="emit('close')">
          <template #icon>
            <NIcon><IconClose /></NIcon>
          </template>
        </NButton>
      </div>
    </div>

    <NAlert v-if="errorMessage" type="error" :title="errorMessage" class="chat-error" />

    <div
      ref="messagesContainer"
      class="chat-content-area"
      @scroll="updateGoToBottomButtonVisibility"
    >
      <div v-if="isThreadListView" class="threads-panel" data-tour="chat.threads">
        <div class="thread-list-header">
          <NText depth="3">Recent Threads</NText>
          <NButton size="tiny" quaternary :loading="threadsLoading" @click="fetchThreads">
            Refresh
          </NButton>
        </div>

        <div v-if="threadsLoading" class="thread-list-loading">
          <NSpin size="small" />
          <NText depth="3">Loading threads...</NText>
        </div>

        <NAlert
          v-else-if="threadsError"
          type="error"
          :title="threadsError"
          class="thread-list-error"
        />

        <div v-else-if="sortedThreads.length === 0" class="thread-list-empty">
          <NText depth="3">No threads yet.</NText>
        </div>

        <div v-else class="thread-list">
          <div
            v-for="thread in sortedThreads"
            :key="thread.uid"
            class="thread-list-item"
            :class="{
              'thread-list-item-loading':
                threadLoadingUid === thread.uid || threadDeleteLoadingUid === thread.uid,
            }"
            role="button"
            tabindex="0"
            @click="handleThreadItemClick(thread)"
            @keydown.enter.prevent="handleThreadItemClick(thread)"
            @keydown.space.prevent="handleThreadItemClick(thread)"
          >
            <div class="thread-list-item-main">
              <div class="thread-list-item-title">
                <n-tooltip v-if="threadAttentionLabel(thread)" placement="top">
                  <template #trigger>
                    <n-icon
                      :size="13"
                      :color="themeVars.errorColor"
                      class="thread-list-item-attention"
                      :aria-label="threadAttentionLabel(thread)"
                    >
                      <icon-alert />
                    </n-icon>
                  </template>
                  {{ threadAttentionLabel(thread) }}
                </n-tooltip>
                {{ threadTitle(thread) }}
              </div>
              <div class="thread-list-item-date">
                {{ formatThreadDate(threadTimestamp(thread)) }}
                <span style="margin-left: 4px" :title="threadModelTitle(thread)">
                  · {{ threadModel(thread) }}
                </span>
                <span
                  v-if="threadReferencedKeys(thread).length > 0"
                  class="thread-list-item-refs"
                  :aria-label="`${threadReferencedKeys(thread).length} files used`"
                >
                  <n-icon :size="12" aria-hidden="true"><paper-clip-outlined /></n-icon>
                  {{ threadReferencedKeys(thread).length }}
                </span>
              </div>
            </div>
            <div class="thread-list-item-actions">
              <NTooltip v-if="isReviewProject">
                <template #trigger>
                  <NButton
                    quaternary
                    circle
                    size="small"
                    :disabled="
                      threadEditSaving ||
                      threadDeleteLoadingUid !== null ||
                      (togglePublicLoadingUid !== null && togglePublicLoadingUid !== thread.uid)
                    "
                    :loading="togglePublicLoadingUid === thread.uid"
                    @click.stop="toggleThreadPublic(thread)"
                  >
                    <template #icon>
                      <NIcon>
                        <IconUnlock v-if="thread.is_public" />
                        <IconLock v-else />
                      </NIcon>
                    </template>
                  </NButton>
                </template>
                <div v-if="thread.is_public">This thread is public</div>
                <div v-else>This thread is private. Click to make it public.</div>
              </NTooltip>

              <NButton
                quaternary
                circle
                size="small"
                :disabled="threadEditSaving || threadDeleteLoadingUid !== null"
                @click.stop="openThreadEditModal(thread)"
              >
                <template #icon>
                  <NIcon><IconEdit /></NIcon>
                </template>
              </NButton>

              <NPopconfirm :show-icon="false" @positive-click="() => deleteThreadByUid(thread)">
                <template #trigger>
                  <NButton
                    quaternary
                    circle
                    size="small"
                    type="error"
                    class="thread-delete-button"
                    :loading="threadDeleteLoadingUid === thread.uid"
                    :disabled="
                      threadEditSaving ||
                      (threadDeleteLoadingUid !== null && threadDeleteLoadingUid !== thread.uid)
                    "
                    @click.stop
                  >
                    <template #icon>
                      <NIcon><IconDelete /></NIcon>
                    </template>
                  </NButton>
                </template>
                Deleting this thread will permanently delete the full conversation history and
                related checkpoints. Continue?
              </NPopconfirm>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showWelcomePrompt" class="welcome-state">
        <div class="welcome-inner">
          <h2 class="welcome-title">{{ welcomeTitle }}</h2>
          <p v-if="isTemplateSkillset" class="welcome-hint">
            Convert this document into a GAIA template, add or edit fields, make a clause
            conditional, or check it for issues.
          </p>
        </div>
      </div>

      <div v-if="!isThreadListView && hasMessages" class="chat-stream">
        <div
          v-for="(message, index) in formattedMessages"
          :key="message.id"
          class="chat-message"
          :class="`chat-message-${message.role}`"
        >
          <div class="chat-bubble">
            <span class="chat-role">{{ message.label }}</span>
            <div v-if="message.role === 'assistant'" class="chat-content chat-content-markdown">
              <n-text v-if="message.failed" type="error">{{ message.content }}</n-text>
              <MarkdownRenderer v-else :content="message.content" :code-actions="chatCodeActions" />
              <!-- In-bubble working indicator (where the answer appears) so the
                   bubble never looks empty/stuck while the agent reads/works. -->
              <template v-if="index === formattedMessages.length - 1 && isStreaming">
                <transition-group
                  v-if="visibleStatusGroups.length > 0"
                  tag="div"
                  name="chat-status"
                  class="chat-typing chat-status-groups"
                >
                  <div v-for="group in visibleStatusGroups" :key="group.id">
                    <n-space align="center" :size="6" :wrap-item="false">
                      <n-icon
                        v-if="group.done"
                        :size="14"
                        :color="themeVars.successColor"
                        aria-hidden="true"
                      >
                        <check-circle-outlined />
                      </n-icon>
                      <span v-else class="chat-status-pending-circle" aria-hidden="true" />
                      <span v-if="!group.done" class="chat-typing-text">{{ group.label }}</span>
                      <n-text v-else :depth="3">{{ group.label }}</n-text>
                    </n-space>
                    <n-collapse-transition :show="group.details.length > 0">
                      <div style="padding-left: 20px">
                        <n-text
                          v-for="detail in group.details"
                          :key="detail.id"
                          :depth="3"
                          tag="div"
                        >
                          {{ detail.text }}
                        </n-text>
                      </div>
                    </n-collapse-transition>
                  </div>
                </transition-group>
                <div v-else-if="docToolStatus || message.content.length === 0" class="chat-typing">
                  <span class="chat-typing-text">{{ docToolStatus ?? 'Thinking…' }}</span>
                </div>
              </template>
              <div
                v-if="isOfficeAddin && message.content.trim().length > 0"
                class="chat-message-actions"
              >
                <NButton
                  size="tiny"
                  secondary
                  class="chat-comment-button"
                  :loading="commentingMessageId === message.id"
                  :disabled="
                    isStreaming ||
                    (commentingMessageId !== null && commentingMessageId !== message.id)
                  "
                  @click="addAssistantResponseAsWordComment(message)"
                >
                  <template #icon>
                    <NIcon><IconComment /></NIcon>
                  </template>
                  Add as comment
                </NButton>
              </div>
            </div>
            <template v-else>
              <div
                v-if="message.wordDocumentContext"
                class="word-selection-card word-selection-card-message"
              >
                <div class="word-selection-card-header">
                  <NIcon :size="14" aria-hidden="true"><IconDocumentText /></NIcon>
                  <span>Word document</span>
                </div>
                <div class="word-selection-card-text">Full document included as context.</div>
              </div>
              <div
                v-if="message.wordSelectionContext"
                class="word-selection-card word-selection-card-message"
              >
                <div class="word-selection-card-header">
                  <NIcon :size="14" aria-hidden="true"><IconDocumentText /></NIcon>
                  <span>Word selection</span>
                </div>
                <div class="word-selection-card-text">{{ message.wordSelectionContext }}</div>
              </div>
              <div v-if="message.content" class="chat-content">{{ message.content }}</div>
            </template>

            <div v-if="message.interrupt" class="hitl-panel">
              <HitlInterruptCard
                v-for="interrupt in message.interrupt.interrupts"
                :key="interrupt.interruptId"
                :interrupt="interrupt"
                :status="interrupt.status"
                :error-message="interrupt.errorMessage"
                :disabled="interrupt.status !== 'pending' || isStreaming"
                :active="message.id === activeInterruptMessageId && interrupt.status === 'pending'"
                @submit="payload => submitHitlDecision(interrupt, payload)"
              />
            </div>
          </div>
          <div
            v-if="message.role === 'user' && message.selectedDocumentsIncluded"
            class="message-attachments"
          >
            <template v-if="messageReferencedEntries(message).length > 0">
              <component
                :is="entry.key ? RouterLink : 'span'"
                v-for="(entry, entryIndex) in messageReferencedEntries(message)"
                :key="entry.key ?? `${entry.name}-${entryIndex}`"
                :to="entry.key ? referencedItemRoute(entry.key) : undefined"
                class="message-attachment-card"
                :class="{ 'message-attachment-navigable': entry.key }"
                :aria-label="entry.key ? `Open ${entry.name}` : undefined"
              >
                <span
                  class="message-attachment-tile"
                  :class="{ 'message-attachment-tile-contract': entry.type === 'contract' }"
                >
                  <n-icon :size="14" aria-hidden="true">
                    <file-protect-outlined v-if="entry.type === 'contract'" />
                    <file-text-outlined v-else />
                  </n-icon>
                </span>
                <span class="message-attachment-info">
                  <span class="message-attachment-name">{{ entry.name }}</span>
                  <span class="message-attachment-kind">{{ entry.kind }}</span>
                </span>
              </component>
            </template>
            <span v-else class="message-attachment-card">
              <span class="message-attachment-tile">
                <n-icon :size="14" aria-hidden="true"><paper-clip-outlined /></n-icon>
              </span>
              <span class="message-attachment-info">
                <span class="message-attachment-name">{{ selectedDocumentsSummary(message) }}</span>
              </span>
            </span>
          </div>
        </div>
      </div>

      <NFloatButton v-if="showGoToBottomButton" class="go-to-bottom-button" @click="scrollToBottom">
        <NIcon :size="18" color="#ffffff"><IconArrowDown /></NIcon>
      </NFloatButton>
    </div>

    <div v-if="!isThreadListView" class="composer-wrap">
      <NAlert
        v-if="hasPendingInterrupts"
        type="warning"
        :title="pendingInterruptAlertTitle"
        class="composer-interrupt-alert"
      />
      <div v-if="selectedContextFiles.length > 0" class="composer-context-row">
        <div
          v-for="contextFile in selectedContextFiles"
          :key="contextFile.key"
          class="composer-context-card"
        >
          <div
            v-if="contextFile.status === 'uploading'"
            class="composer-context-icon composer-context-icon-pending"
            :aria-label="`Uploading ${contextFile.name}: ${contextFile.progress}%`"
          >
            <n-progress
              type="circle"
              :percentage="contextFile.progress"
              :show-indicator="false"
              :stroke-width="14"
              style="width: 24px; height: 24px"
            />
          </div>
          <div
            v-else-if="contextFile.status === 'processing'"
            class="composer-context-icon composer-context-icon-pending"
            :aria-label="`Processing ${contextFile.name}`"
          >
            <n-spin :size="18" />
          </div>
          <div
            v-else
            class="composer-context-icon"
            :style="{ background: contextTileColor(contextFile) }"
          >
            <n-icon :size="16" aria-hidden="true">
              <file-protect-outlined v-if="contextFile.type === 'contract'" />
              <robot-outlined v-else-if="contextFile.type === 'gaia'" />
              <file-text-outlined v-else />
            </n-icon>
          </div>
          <div class="composer-context-content">
            <n-ellipsis class="composer-context-name">{{ contextFile.name }}</n-ellipsis>
            <span
              class="composer-context-kind"
              :class="{ 'composer-context-kind-failed': contextFile.status === 'failed' }"
            >
              {{ contextStatusLabel(contextFile) }}
            </span>
          </div>
          <button
            type="button"
            class="composer-context-remove"
            :aria-label="`Remove ${contextFile.name}`"
            @click="removeSelectedContext(contextFile.key)"
          >
            <n-icon :size="14" aria-hidden="true"><close-outlined /></n-icon>
          </button>
        </div>
      </div>
      <div v-if="isReviewProject && tableSelectedRevisionCount > 0" class="composer-context-row">
        <div class="composer-context-chip">
          <NIcon :size="32" class="composer-context-icon-file"><IconDocument /></NIcon>
          <div class="composer-context-content">
            <div class="composer-context-name">
              {{ tableSelectedRevisionCount }} file{{ tableSelectedRevisionCount === 1 ? '' : 's' }}
              selected
            </div>
          </div>
          <button
            type="button"
            class="composer-context-remove"
            aria-label="Clear selected files"
            @click="clearSelectedRevisions"
          >
            <NIcon :size="14" aria-hidden="true"><IconClose /></NIcon>
          </button>
        </div>
      </div>

      <div
        v-if="!hasPendingInterrupts"
        class="composer-shell"
        data-tour="chat.composer"
        :class="{
          'composer-shell-with-context': hasComposerContext,
          'composer-shell-dragging': isDraggingFiles,
        }"
        :style="{ '--composer-mode-color': confirmationModeColor }"
        @dragover="handleComposerDragOver"
        @dragleave="handleComposerDragLeave"
        @drop="handleComposerDrop"
      >
        <input
          v-if="canAttachFiles"
          ref="composerFileInput"
          type="file"
          multiple
          aria-label="Attach files to this chat"
          class="composer-file-input"
          @change="handleComposerFilesPicked"
        />
        <div v-if="isDraggingFiles" class="composer-drop-overlay">
          <n-text depth="2">Drop files to attach them to this chat</n-text>
        </div>
        <Transition name="word-selection-fly">
          <div v-if="selectedWordText" class="word-selection-card word-selection-card-composer">
            <div class="word-selection-card-header">
              <NIcon :size="14" aria-hidden="true"><IconDocumentText /></NIcon>
              <span>Word selection</span>
              <button
                type="button"
                class="word-selection-card-remove"
                aria-label="Remove selected Word text"
                @click="removeSelectedWordText"
              >
                <NIcon :size="12" aria-hidden="true"><IconClose /></NIcon>
              </button>
            </div>
            <div class="word-selection-card-text">{{ selectedWordText }}</div>
          </div>
        </Transition>

        <div class="composer-input-row">
          <NPopover
            v-model:show="showModelMenu"
            trigger="click"
            placement="top-start"
            :show-arrow="false"
          >
            <template #trigger>
              <NTooltip placement="top" trigger="hover">
                <template #trigger>
                  <button
                    type="button"
                    class="composer-plus"
                    data-tour="chat.model-button"
                    aria-label="Chat settings: mode and model"
                    :style="{ color: confirmationModeColor }"
                  >
                    <NIcon :size="16" aria-hidden="true"><IconSettings /></NIcon>
                  </button>
                </template>
                Mode: {{ confirmationModeLabel }} · Model settings
              </NTooltip>
            </template>

            <div class="chat-model-menu" data-tour="chat.model-menu">
              <NSpace vertical :size="8" class="chat-mode-section">
                <span class="chat-mode-section-title">Confirmation mode</span>
                <div class="chat-mode-segments" role="radiogroup" aria-label="Confirmation mode">
                  <button
                    v-for="option in confirmationModeOptions"
                    :key="option.mode"
                    type="button"
                    class="chat-mode-segment"
                    :class="{ 'chat-mode-segment-active': option.mode === currentConfirmationMode }"
                    :style="option.mode === currentConfirmationMode ? { color: option.color } : {}"
                    role="radio"
                    :aria-checked="option.mode === currentConfirmationMode"
                    @click="handleSelectConfirmationMode(option.mode)"
                  >
                    <NIcon :size="14" aria-hidden="true"><component :is="option.icon" /></NIcon>
                    {{ option.label }}
                  </button>
                </div>
                <span class="chat-mode-description">{{ confirmationModeDescription }}</span>
              </NSpace>

              <NDivider class="chat-mode-divider" />

              <div class="chat-model-menu-header">
                <div class="chat-model-menu-title">Chat model</div>
                <div class="chat-model-menu-current">
                  <template v-if="displayedChatModel">
                    {{
                      formatModelProviderLabel(
                        displayedChatModel.provider,
                        displayedChatModel.model
                      )
                    }}
                    /
                    {{ formatModelLabel(displayedChatModel.model, displayedChatModel.provider) }}
                  </template>
                  <template v-else>Current model unavailable</template>
                </div>
              </div>

              <!-- An open thread keeps the model it started with, so the picker
                   gives way to the reason it is gone. -->
              <div v-if="pinnedChatModel" class="chat-model-menu-empty">
                Pinned to this conversation. Start a new chat to use a different model.
              </div>

              <div v-else-if="modelsLoading" class="chat-model-menu-loading">
                <NSpin size="small" />
                <span>Loading models...</span>
              </div>

              <NAlert
                v-else-if="modelMenuError"
                type="error"
                :show-icon="false"
                class="chat-model-menu-error"
              >
                {{ modelMenuError }}
              </NAlert>

              <div v-else-if="availableChatModels.length === 0" class="chat-model-menu-empty">
                No models available
              </div>

              <div v-else class="chat-model-menu-body">
                <div
                  class="chat-model-provider-list"
                  role="tablist"
                  aria-label="Chat model providers"
                >
                  <button
                    v-for="providerGroup in chatModelProviderGroups"
                    :key="providerGroup.provider"
                    type="button"
                    class="chat-model-provider-item"
                    :class="{
                      'chat-model-provider-item-active':
                        providerGroup.provider === selectedModelProviderLabel,
                    }"
                    @click="selectModelProviderGroup(providerGroup.provider)"
                  >
                    <span>{{ providerGroup.label }}</span>
                    <span class="chat-model-provider-item-count">{{
                      providerGroup.models.length
                    }}</span>
                  </button>
                </div>

                <div v-if="filteredChatModels.length === 0" class="chat-model-menu-empty">
                  No models available for this provider
                </div>

                <div v-else class="chat-model-menu-list">
                  <button
                    v-for="modelOption in filteredChatModels"
                    :key="getModelOptionKey(modelOption)"
                    type="button"
                    class="chat-model-menu-item"
                    :class="{
                      'chat-model-menu-item-active': isCurrentChatModel(modelOption),
                    }"
                    :disabled="!!selectingModelKey || isCurrentChatModel(modelOption)"
                    @click="handleSelectChatModel(modelOption)"
                  >
                    <span class="chat-model-menu-item-main">
                      <span class="chat-model-menu-item-name">
                        {{ formatModelLabel(modelOption.model, modelOption.provider) }}
                      </span>
                      <span class="chat-model-menu-item-provider">
                        {{ formatModelProviderLabel(modelOption.provider, modelOption.model) }}
                      </span>
                    </span>
                    <span class="chat-model-menu-item-status">
                      {{
                        selectingModelKey === getModelOptionKey(modelOption)
                          ? '...'
                          : isCurrentChatModel(modelOption)
                            ? '✓'
                            : ''
                      }}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </NPopover>
          <NTooltip v-if="canAttachFiles" placement="top" trigger="hover">
            <template #trigger>
              <button
                type="button"
                class="composer-plus"
                data-tour="chat.attach"
                aria-label="Attach files"
                :disabled="isStreaming"
                @click="openComposerFilePicker"
              >
                <NIcon :size="18" aria-hidden="true"><IconAttach /></NIcon>
              </button>
            </template>
            Attach files
          </NTooltip>
          <gaia-prompt-textarea
            v-model:value="input"
            class="composer-input"
            :bordered="false"
            :autosize="{ minRows: 1, maxRows: 4 }"
            placeholder="Message the AI"
            :disabled="isStreaming"
            @submit="sendMessage"
          />
          <NButton
            v-if="isStreaming"
            circle
            class="composer-send composer-stop"
            :disabled="false"
            @click="stopStreaming"
          >
            <template #icon>
              <span class="composer-stop-icon-square" aria-hidden="true"></span>
            </template>
          </NButton>
          <NButton v-else circle class="composer-send" :disabled="!canSend" @click="sendMessage">
            <template #icon>
              <NIcon><IconArrowUp /></NIcon>
            </template>
          </NButton>
        </div>
      </div>

      <div v-if="composerPendingLabel" class="composer-pending-row" aria-live="polite">
        <n-spin :size="12" />
        <n-text depth="3">{{ composerPendingLabel }}</n-text>
      </div>

      <div class="composer-meta-row" aria-live="polite">
        <div class="composer-meta-primary">
          <span class="composer-meta-label">Mode:</span>
          {{ confirmationModeLabel }}
          <span class="composer-meta-label composer-meta-label-spaced">Model:</span>
          <template v-if="displayedChatModel">
            {{ formatModelProviderLabel(displayedChatModel.provider, displayedChatModel.model) }}
            /
            {{ formatModelLabel(displayedChatModel.model, displayedChatModel.provider) }}
          </template>
          <template v-else>Unavailable</template>
        </div>
        <span v-if="isTemplateSkillset" class="composer-meta-hint">
          Conversion requests may take a little while.
        </span>
      </div>
    </div>

    <NModal
      v-model:show="showThreadEditModal"
      preset="card"
      title="Edit Thread"
      :style="{ width: 'min(640px, calc(100vw - 48px))' }"
      :mask-closable="!threadEditSaving"
      @update:show="!$event && closeThreadEditModal()"
    >
      <div class="thread-edit-form">
        <div class="thread-edit-field">
          <NText class="thread-edit-label">Title</NText>
          <gaia-input v-model:value="editThreadTitle" placeholder="Enter thread title" />
        </div>

        <div class="thread-edit-field">
          <NText class="thread-edit-label">Thread Metadata (JSON)</NText>
          <gaia-textarea
            v-model:value="editThreadMetadataText"
            :autosize="{ minRows: 4, maxRows: 10 }"
            placeholder='Optional JSON object, e.g. {"source":"gaia"}'
          />
        </div>

        <div class="thread-edit-archive-row">
          <NText class="thread-edit-label">Archive Thread</NText>
          <gaia-switch v-model:value="editThreadIsArchived" :disabled="threadEditSaving" />
        </div>

        <NAlert v-if="threadEditError" type="error" :title="threadEditError" />
      </div>

      <template #footer>
        <div class="thread-edit-footer">
          <NButton :disabled="threadEditSaving" @click="closeThreadEditModal">Cancel</NButton>
          <NButton type="primary" :loading="threadEditSaving" @click="saveThreadEdits">
            Update
          </NButton>
        </div>
      </template>
    </NModal>
  </div>
</template>

<style scoped>
.gaia-chat-view {
  --chat-bg: #ffffff;
  --chat-border: rgba(17, 24, 39, 0.08);
  --chat-text: #111827;
  --chat-muted: rgba(17, 24, 39, 0.56);
  --chat-panel: #ffffff;
  --chat-shadow: 0 8px 20px rgba(17, 24, 39, 0.08);

  height: calc(100vh - 130px);
  min-height: 640px;
  display: flex;
  flex-direction: column;
  background: var(--chat-bg) !important;
  border-radius: 0;
  border: none;
  padding: 14px 16px 18px;
}

.gaia-chat-view-sidebar {
  height: 100%;
  min-height: 0;
}

html.dark .gaia-chat-view {
  --chat-bg: #0f1218;
  --chat-border: rgba(255, 255, 255, 0.1);
  --chat-text: #f8fafc;
  --chat-muted: rgba(248, 250, 252, 0.6);
  --chat-panel: rgba(15, 23, 42, 0.88);
  --chat-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
}

.chat-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 44px;
  margin: 0 -16px;
  padding: 8px 16px;
  border-bottom: 2px solid var(--chat-border);
}

.toolbar-left {
  min-width: 0;
  flex: 1 1 auto;
}

.toolbar-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--chat-muted);
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  display: block;
  width: 100%;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.new-chat-button {
  --n-color: #111827 !important;
  --n-color-hover: #0b0f19 !important;
  --n-color-pressed: #06090f !important;
  --n-color-focus: #111827 !important;
  --n-color-disabled: #111827 !important;
  --n-border: 1px solid #111827 !important;
  --n-border-hover: 1px solid #0b0f19 !important;
  --n-border-pressed: 1px solid #06090f !important;
  --n-border-focus: 1px solid #111827 !important;
  --n-border-disabled: 1px solid #111827 !important;
  --n-text-color: #ffffff !important;
  --n-text-color-hover: #ffffff !important;
  --n-text-color-pressed: #ffffff !important;
  --n-text-color-focus: #ffffff !important;
  --n-text-color-disabled: #ffffff !important;
  --n-ripple-color: rgba(255, 255, 255, 0.18) !important;
}

.new-chat-button:focus-visible {
  box-shadow: 0 0 0 2px rgba(17, 24, 39, 0.2);
}

.chat-error {
  margin-top: 10px;
}

.chat-content-area {
  position: relative;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding-top: 16px;
  background: var(--chat-bg);
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.chat-content-area::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.welcome-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 100px;
}

.welcome-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  max-width: 460px;
}

.welcome-title {
  margin: 0;
  font-size: 34px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--chat-text);
  text-align: center;
}

.welcome-hint {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--chat-muted);
  text-align: center;
}

.chat-stream {
  width: min(940px, 100%);
  margin: 0 auto;
  padding-bottom: 16px;
}

.go-to-bottom-button {
  position: sticky !important;
  align-self: center;
  left: auto !important;
  right: auto !important;
  bottom: 10px !important;
  transform: none;
  z-index: 5;
  --n-color: #111827 !important;
  --n-color-hover: #111827 !important;
  --n-color-pressed: #111827 !important;
  --n-text-color: #ffffff !important;
}

.threads-panel {
  width: min(900px, 100%);
  margin: 0 auto;
  background: transparent;
  border-radius: 0;
  border: none;
  padding: 14px;
  box-shadow: none;
}

.thread-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.thread-list-loading,
.thread-list-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px 0;
}

.thread-list-error {
  margin-top: 4px;
}

.thread-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  flex: 1;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.thread-list::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.thread-list-item {
  width: 100%;
  text-align: left;
  background: var(--chat-panel);
  border: 1px solid var(--chat-border);
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
}

.thread-list-item-main {
  min-width: 0;
  flex: 1;
}

.thread-list-item-loading {
  opacity: 0.6;
  pointer-events: none;
}

.thread-list-item:hover {
  background: rgba(37, 99, 235, 0.04);
}

html.dark .thread-list-item:hover {
  background: rgba(37, 99, 235, 0.08);
}

.thread-list-item-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--chat-text);
  margin-bottom: 4px;
  line-height: 1.3;
}

/* Flex so the attachment badge's icon centres on the date text instead of
   riding the baseline. */
.thread-list-item-date {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: var(--chat-muted);
}

.thread-list-item-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.thread-delete-button {
  --n-text-color: #ef4444 !important;
  --n-text-color-hover: #ef4444 !important;
  --n-text-color-pressed: #ef4444 !important;
}

.thread-edit-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.thread-edit-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.thread-edit-archive-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 2px;
}

.thread-edit-label {
  font-size: 13px;
  font-weight: 600;
}

.thread-edit-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.chat-message {
  display: flex;
  margin-bottom: 12px;
}

.chat-message-user {
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
}

.chat-message-assistant {
  align-items: flex-start;
  justify-content: flex-start;
}

.chat-bubble {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.chat-message-user .chat-bubble {
  max-width: min(74%, 760px);
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(37, 99, 235, 0.12);
  box-shadow: var(--chat-shadow);
}

.chat-message-assistant .chat-bubble {
  max-width: 100%;
}

.chat-role {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--chat-muted);
}

.chat-content {
  white-space: pre-wrap;
  line-height: 1.5;
  color: var(--chat-text);
}

.chat-content-markdown {
  white-space: normal;
}

.chat-content-markdown :deep(.markdown-content) {
  color: var(--chat-text);
  line-height: 1.5;
}

.chat-content-markdown :deep(.markdown-content > :first-child) {
  margin-top: 0;
}

.chat-content-markdown :deep(.markdown-content > :last-child) {
  margin-bottom: 0;
}

.chat-message-actions {
  display: flex;
  justify-content: flex-start;
  margin-top: 10px;
}

.chat-comment-button {
  --n-border-radius: 6px !important;
}

.hitl-panel {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.composer-wrap {
  padding-top: 14px;
}

.composer-interrupt-alert {
  width: min(640px, 100%);
  margin: 0 auto 10px;
  --n-color: rgba(59, 130, 246, 0.08) !important;
  --n-border: 1px solid rgba(59, 130, 246, 0.28) !important;
  --n-icon-color: #2563eb !important;
  --n-title-text-color: var(--chat-text) !important;
  --n-content-text-color: var(--chat-text) !important;
}

.composer-interrupt-alert :deep(.n-alert-body__title) {
  font-size: 13px;
  line-height: 1.4;
}

html.dark .composer-interrupt-alert {
  --n-color: rgba(30, 64, 175, 0.2) !important;
  --n-border: 1px solid rgba(96, 165, 250, 0.35) !important;
  --n-icon-color: #60a5fa !important;
}

/* One input surface: the shell alone carries the border, the mode colour and the
   focus state (the inner NInput is borderless). Idle shows a hairline border tinted
   toward the mode colour; focus adds a soft halo of the same colour instead of a
   second border, so the mode reads as an accent rather than a cage. */
.composer-shell {
  width: min(640px, 100%);
  margin: 0 auto;
  min-height: 54px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  border-radius: 999px;
  border: 1px solid
    color-mix(in srgb, var(--composer-mode-color, var(--chat-border)) 55%, var(--chat-border));
  background: var(--chat-panel);
  box-shadow: var(--chat-shadow);
  padding: 8px 10px 8px 12px;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.composer-shell:focus-within {
  border-color: var(--composer-mode-color, var(--chat-border));
  box-shadow:
    var(--chat-shadow),
    0 0 0 3px color-mix(in srgb, var(--composer-mode-color, var(--chat-border)) 18%, transparent);
}

.composer-shell-with-context {
  border-radius: 20px;
  padding: 10px 10px 8px 12px;
}

/* Attachment cards in a single horizontal strip that scrolls sideways on overflow
   (capped at 5 items, so scrolling is the exception, not the norm). */
.composer-context-row {
  width: min(640px, 100%);
  margin: 0 auto;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  /* Bottom padding separates the cards from the composer shell below them. */
  padding: 8px 2px 10px 2px;
}

.composer-context-card {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  min-width: 150px;
  max-width: 240px;
  border: 1px solid var(--chat-border);
  border-radius: 12px;
  background: var(--chat-panel);
  padding: 6px 8px;
}

/* The review-project branch still uses the single full-width chip. */
.composer-context-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 1px solid var(--chat-border);
  border-radius: 12px;
  background: var(--chat-panel);
  padding: 7px 8px;
}

/* Sized for a single-line row; a 32px tile made the chip taller than its text. */
.composer-context-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ef4444;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}

/* Pending references show a spinner in place of the tile, so the chip reads as
   in-flight rather than as a document that is ready to be asked about. */
.composer-context-icon-pending {
  background: transparent;
}

.composer-file-input {
  display: none;
}

/* Popover listing the thread's referenced files; padding only, Naive styles the rest. */
.referenced-files-title {
  display: block;
  padding: 4px 4px 8px 4px;
}

.referenced-files-list {
  max-height: 260px;
  min-width: 220px;
}

.referenced-files-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  border-radius: 6px;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}

.referenced-files-row:hover {
  background: rgba(17, 24, 39, 0.06);
}

.referenced-files-row-static {
  cursor: default;
}

.referenced-files-row-static:hover {
  background: transparent;
}

.referenced-files-kind {
  font-size: 11px;
  color: var(--chat-muted);
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
}

/* Attachment cards below a user bubble — detached, edge-aligned with the bubble,
   in the composer cards' visual language. Navigable cards are real buttons. */
.message-attachments {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 6px;
  max-width: 85%;
}

.message-attachment-card {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 250px;
  padding: 5px 10px 5px 6px;
  border: 1px solid var(--chat-border);
  border-radius: 12px;
  background: var(--chat-panel);
  color: var(--chat-muted);
  font: inherit;
  text-align: left;
}

.message-attachment-tile {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #6b7280;
  color: #ffffff;
  flex-shrink: 0;
}

.message-attachment-tile-contract {
  background: #f59e0b;
}

.message-attachment-info {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.message-attachment-name {
  font-size: 12px;
  color: var(--chat-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-attachment-kind {
  font-size: 10px;
  color: var(--chat-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.message-attachment-navigable {
  text-decoration: none;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.message-attachment-navigable:hover {
  border-color: var(--chat-muted);
  box-shadow: 0 2px 8px rgba(17, 24, 39, 0.08);
}

.referenced-files-name {
  font-size: 13px;
  max-width: 260px;
}

.thread-files-trigger {
  display: inline-flex;
}

.thread-list-item-refs {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: 8px;
}

/* Leading marker on a thread whose run is waiting for the user. Nudged down so
   it centres on the title's cap height rather than its baseline. */
.thread-list-item-attention {
  margin-right: 6px;
  vertical-align: -2px;
}

.composer-shell-dragging {
  position: relative;
}

.composer-drop-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed var(--chat-muted);
  border-radius: 12px;
  background: var(--chat-panel);
  pointer-events: none;
}

.composer-pending-row {
  width: min(640px, 100%);
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 2px 0 2px;
}

.composer-context-icon-file {
  background: transparent;
  color: var(--chat-text);
  flex-shrink: 0;
}

/* Two lines — name over kind/status — with min-width: 0 so the name ellipsises;
   flex: 1 pushes the remove button to the card's right edge. */
.composer-context-content {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.composer-context-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--chat-text);
  max-width: 100%;
}

.composer-context-kind {
  font-size: 11px;
  color: var(--chat-muted);
  text-transform: uppercase;
  white-space: nowrap;
}

.composer-context-kind-failed {
  color: #dc2626;
}

.composer-context-remove {
  border: none;
  background: transparent;
  color: var(--chat-muted);
  cursor: pointer;
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 14px;
  line-height: 1;
  border-radius: 50%;
  flex-shrink: 0;
}

.composer-context-remove:hover {
  background: rgba(17, 24, 39, 0.08);
  color: var(--chat-text);
}

.word-selection-card {
  border: 1px solid rgba(37, 99, 235, 0.28);
  border-radius: 8px;
  background: rgba(37, 99, 235, 0.06);
  color: var(--chat-text);
}

.word-selection-card-composer {
  margin: 0 2px;
  transform-origin: bottom center;
}

.word-selection-card-message {
  margin-bottom: 10px;
}

.word-selection-fly-enter-active,
.word-selection-fly-leave-active {
  overflow: hidden;
  transition:
    opacity 0.22s ease,
    transform 0.22s ease,
    max-height 0.22s ease,
    margin 0.22s ease;
}

.word-selection-fly-enter-from,
.word-selection-fly-leave-to {
  max-height: 0;
  margin-top: 0;
  margin-bottom: 0;
  opacity: 0;
  transform: translateY(18px) scale(0.98);
}

.word-selection-fly-enter-to,
.word-selection-fly-leave-from {
  max-height: 220px;
  opacity: 1;
  transform: translateY(0) scale(1);
}

.word-selection-card-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px 0;
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
}

.word-selection-card-text {
  max-height: 150px;
  overflow-y: auto;
  padding: 6px 10px 10px;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
}

.word-selection-card-remove {
  margin-left: auto;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
  color: var(--chat-muted);
  cursor: pointer;
}

.word-selection-card-remove:hover {
  background: rgba(37, 99, 235, 0.12);
  color: #2563eb;
}

html.dark .word-selection-card {
  border-color: rgba(96, 165, 250, 0.35);
  background: rgba(37, 99, 235, 0.16);
}

html.dark .word-selection-card-header {
  color: #93c5fd;
}

.composer-input-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.composer-meta-row {
  width: min(640px, 100%);
  margin: 6px auto 0;
  min-height: 28px;
  padding: 0 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 2px;
}

.composer-meta-primary {
  font-size: 12px;
  line-height: 1.3;
  color: var(--chat-muted);
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.composer-meta-label {
  margin-right: 6px;
  font-weight: 600;
  color: var(--chat-text);
}

.composer-meta-label-spaced {
  margin-left: 10px;
}

.composer-meta-hint {
  font-size: 11px;
  line-height: 1.3;
  color: var(--chat-muted);
  font-style: italic;
}

.composer-plus {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  font-size: 22px;
  line-height: 1;
  color: var(--chat-muted);
  background: transparent;
  cursor: pointer;
}

.composer-plus:hover {
  background: rgba(15, 23, 42, 0.06);
  color: var(--chat-text);
}

.composer-plus:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.composer-plus:disabled:hover {
  background: transparent;
  color: var(--chat-muted);
}

.chat-model-menu {
  width: min(360px, calc(100vw - 40px));
  height: clamp(340px, 56vh, 460px);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.chat-mode-section-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--chat-text);
}

.chat-mode-divider {
  margin: 0 !important;
}

/* Segmented mode picker: every mode visible at once on a recessed track, the
   active one lifted onto a card and coloured by its mode (inline style). The
   popover teleports to body, so colours are concrete + html.dark, not vars. */
.chat-mode-segments {
  display: flex;
  gap: 4px;
  width: 100%;
  padding: 4px;
  border-radius: 10px;
  background: rgba(17, 24, 39, 0.06);
}

.chat-mode-segment {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 10px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: rgba(17, 24, 39, 0.55);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;
}

.chat-mode-segment:hover {
  color: rgba(17, 24, 39, 0.85);
}

.chat-mode-segment-active {
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(17, 24, 39, 0.14);
}

.chat-mode-description {
  display: block;
  font-size: 12px;
  line-height: 1.4;
  color: rgba(17, 24, 39, 0.55);
}

html.dark .chat-mode-segments {
  background: rgba(255, 255, 255, 0.08);
}

html.dark .chat-mode-segment {
  color: rgba(255, 255, 255, 0.6);
}

html.dark .chat-mode-segment:hover {
  color: rgba(255, 255, 255, 0.85);
}

html.dark .chat-mode-segment-active {
  background: rgba(255, 255, 255, 0.14);
  box-shadow: none;
}

html.dark .chat-mode-description {
  color: rgba(255, 255, 255, 0.55);
}

.chat-model-menu-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.chat-model-menu-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--chat-text);
}

.chat-model-menu-current {
  font-size: 12px;
  color: var(--chat-muted);
  line-height: 1.35;
  word-break: break-word;
}

.chat-model-menu-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--chat-muted);
  font-size: 13px;
  min-height: 36px;
  flex: 1;
}

.chat-model-menu-error {
  margin: 0;
}

.chat-model-menu-empty {
  color: var(--chat-muted);
  font-size: 13px;
  min-height: 36px;
  display: flex;
  align-items: center;
  flex: 1;
}

.chat-model-menu-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.chat-model-provider-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  padding-bottom: 10px;
  margin-bottom: 6px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.14);
}

.chat-model-provider-item {
  width: 100%;
  border: 1px solid var(--chat-border);
  background: transparent;
  border-radius: 999px;
  padding: 6px 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--chat-text);
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  min-width: 0;
}

.chat-model-provider-item:hover {
  background: rgba(15, 23, 42, 0.04);
}

.chat-model-provider-item-active {
  border-color: rgba(37, 99, 235, 0.35);
  background: rgba(59, 130, 246, 0.08);
  color: #1d4ed8;
}

.chat-model-provider-item-count {
  min-width: 1.2em;
  text-align: center;
  color: var(--chat-muted);
  font-size: 11px;
}

.chat-model-menu-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 0;
  overflow-y: auto;
}

.chat-model-menu-item {
  width: 100%;
  border: 1px solid var(--chat-border);
  background: transparent;
  border-radius: 10px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: left;
  color: var(--chat-text);
  cursor: pointer;
}

.chat-model-menu-item:hover:not(:disabled) {
  background: rgba(15, 23, 42, 0.04);
}

.chat-model-menu-item:disabled {
  cursor: default;
  opacity: 0.8;
}

.chat-model-menu-item-active {
  border-color: rgba(37, 99, 235, 0.35);
  background: rgba(59, 130, 246, 0.08);
}

.chat-model-menu-item-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.chat-model-menu-item-name {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.25;
  word-break: break-word;
}

.chat-model-menu-item-provider {
  font-size: 11px;
  color: var(--chat-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.chat-model-menu-item-status {
  flex-shrink: 0;
  color: #2563eb;
  font-size: 13px;
  font-weight: 700;
  min-width: 12px;
  text-align: center;
}

.composer-input {
  flex: 1;
}

.composer-input :deep(.n-input) {
  background: transparent !important;
}

.composer-input :deep(.n-input-wrapper) {
  box-shadow: none !important;
  background: transparent !important;
}

.composer-input :deep(textarea) {
  color: var(--chat-text);
}

.composer-send {
  background: #111827;
  color: #ffffff;
}

.composer-send:hover {
  background: #0f172a;
}

.composer-stop {
  background: #111827;
}

.composer-stop:hover {
  background: #0f172a;
}

.composer-stop-icon-square {
  width: 12px;
  height: 12px;
  display: inline-block;
  background: currentColor;
  border-radius: 2px;
}

.composer-send:disabled {
  background: rgba(15, 23, 42, 0.35);
  color: rgba(255, 255, 255, 0.85);
}

.chat-typing {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--chat-muted);
}

/* Spacing when the working indicator follows already-streamed content. */
.chat-content-markdown > .chat-typing:not(:first-child) {
  margin-top: 8px;
}

/* Shimmer the status text (Claude/ChatGPT style): a brighter band sweeps
   across muted text so the wait reads as "alive" rather than stuck. */
.chat-typing-text {
  font-weight: 500;
  background: linear-gradient(
    90deg,
    var(--chat-muted) 0%,
    var(--chat-muted) 35%,
    var(--chat-text) 50%,
    var(--chat-muted) 65%,
    var(--chat-muted) 100%
  );
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  animation: chat-typing-shimmer 1.6s linear infinite;
}

@keyframes chat-typing-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

/* Live status groups: a vertical trail of "what the agent is doing" lines.
   Vue's transition-group fades old finished groups out at the top and new ones
   in at the bottom (Naive has no list-transition primitive). */
.chat-status-groups {
  position: relative;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}

.chat-status-enter-active,
.chat-status-leave-active,
.chat-status-move {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.chat-status-enter-from {
  opacity: 0;
  transform: translateY(4px);
}

.chat-status-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Take leaving rows out of flow so the remaining ones slide up smoothly. */
.chat-status-leave-active {
  position: absolute;
  width: 100%;
}

/* Open-group marker: an empty ring still waiting for its checkmark. */
.chat-status-pending-circle {
  box-sizing: border-box;
  width: 12px;
  height: 12px;
  margin: 1px;
  border: 1.5px solid var(--chat-muted);
  border-radius: 50%;
}

@media (prefers-reduced-motion: reduce) {
  .chat-typing-text {
    animation: none;
    -webkit-text-fill-color: var(--chat-muted);
    color: var(--chat-muted);
  }

  .chat-status-enter-active,
  .chat-status-leave-active,
  .chat-status-move {
    transition: none;
  }
}

@media (max-width: 1100px) {
  .gaia-chat-view {
    height: calc(100vh - 120px);
    min-height: 540px;
  }

  .welcome-title {
    font-size: 28px;
  }
}
</style>
